import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useHistory } from 'react-router'
import { useSelector } from 'react-redux'
import './PlayScreen.css'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import MakeContext from '../Make/MakeContext'
import ContentEngine from '../ContentEngine/ContentEngine'
import { GrPrevious, GrNext } from 'react-icons/gr'
import renderImage from '../../source/importImg'
import renderPrgImage from '../../source/programImg'
import renderCompImg from '../../source/Comp_Img'
import SliderRange from '../ReusableComponents/Slider/Slider'
import RGBSlider from './RGBSlider'
import MotorSlider from './MotorSlider'
import LiveButtonControl from './LiveButtonControl'
import LiveTackButtonControl from './LiveTackButtonControl'
import LiveHoldButtonControl from './LiveHoldButtonControl'
import LiveTriggerButtonControl from './LiveTriggerButtonControl'
import LiveAckButtonControl from './LiveAckButtonControl'
import LiveSpamButtonControl from './LiveSpamButtonControl'
import LivePressReleaseButtonControl from './LivePressReleaseButtonControl'
import LiveRangeSliderControl from './LiveRangeSliderControl'
import '@google/model-viewer'
import getBackendImage from '../../source/imageUtil'
import { IoInformationCircle } from 'react-icons/io5'

import {
    MdOutlineArrowBack,
    MdOutlineViewInAr,
    MdFullscreen,
    MdClosedCaption,
} from 'react-icons/md'
import { BsStars } from 'react-icons/bs'
import { RxReset } from 'react-icons/rx'
import { useLocation } from "react-router-dom";
const BACKEND_URL = process.env.REACT_APP_API_BASE

// --- Momentary-button serialization tuning ---
// The board reports "Rdone" when the action has finished and replies "busy" if
// a command arrives while one is still running. "Rdone" is the primary release
// signal; the timer below is only a safety net for a reply that never arrives,
// and a "busy" reply re-locks if it fires too early.
const ACTION_FALLBACK_MS = 5000
// A "busy" reply is only treated as ours (→ re-lock) if a tack press happened
// within this window; otherwise it's an unrelated/boot-time busy and ignored.
const PRESS_RELEVANCE_MS = 6000

const Ports = (port) => [0, 0]

// Normalise a step's live field to dict format.
// Old schema stored live as an array: [{inputId, ...}, ...]
// New schema stores live as a dict:   { inputId: {...}, ... }
const normalizeLive = (live) => {
    if (!live) return {}
    const applyDefaults = (entry) => ({
        isFree: true,
        compsIds: {},
        extraTargets: [],
        ...entry,
    })
    if (Array.isArray(live)) {
        return live.reduce((dict, entry) => {
            const { inputId, ...rest } = entry
            if (inputId) dict[inputId] = applyDefaults(rest)
            return dict
        }, {})
    }
    return Object.fromEntries(
        Object.entries(live).map(([k, v]) => [k, applyDefaults(v)])
    )
}

// Shared numeric comparison helper used by evaluateCondition below
const applyNumericOperator = (currentValue, operator, targetValue) => {
    const target = Number(targetValue)
    switch (operator) {
        case '==':
            return currentValue === target
        case '>':
            return currentValue > target
        case '<':
            return currentValue < target
        case '>=':
            return currentValue >= target
        case '<=':
            return currentValue <= target
        case '!=':
            return currentValue !== target
        default:
            return false
    }
}

function PlayScreen({
    data: dataProp,
    projectId: propProjectId,
    screenId: propScreenId,
    initialStep,
}) {
    const history = useHistory()
    const location = useLocation();
    // --- NEW: Parse URL inside the component for dynamic preview routing ---
    const urlParams = new URLSearchParams(
        history.location?.search || window.location.search
    )
    const isPreviewMode = urlParams.get('preview') === 'true'
    const queryProjectId = urlParams.get('projectId')
    const screenId = propScreenId || urlParams.get('screenId') || sessionStorage.getItem('playScreenId') || null

    const makeCtx = useContext(MakeContext)
    const isMake = !!makeCtx
    const { setHeaderProps, setFooterProps, setToolbarProps } = makeCtx || {}
    const isShellMode = isMake && !!setHeaderProps

    const HeaderComponent = MakeHeader

    // Check if we're in confined play mode (from KitMenu Play button)
    const isConfinedPlay = sessionStorage.getItem('isConfinedPlay') === 'true'
    const subprogramId = sessionStorage.getItem('playSubprogramId')

    // When launched from the Kit Menu Play button, the final injected
    // "Congratulations" step is skipped. Only applies to standalone route play
    // (never to embedded dataProp flows like ContentEngine / shell mode).
    const hideFinalStep =
        !dataProp && sessionStorage.getItem('playHideFinalStep') === 'true'

    // Fallback order: Props -> URL Query (Admin) -> Session (Normal) -> Default
    const projectId =
        propProjectId ||
        (dataProp
            ? dataProp.projectId
            : queryProjectId || subprogramId || 'talking-dragon')

    const modelRefs = useRef({})
    const prevOutputs = useRef({})
    const audioRef = useRef(null)
    const appContainerRef = useRef(null)
    const safeAreaRef = useRef(null)
    const prevLiveStatesRef = useRef({})
    // Always-current snapshot of outputStates, used for seeding prevLiveStatesRef on step entry.
    const outputStatesRef = useRef({})

    // Hardware access – used to send the global settingbytes on page load
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)

    // Tracks pending timers for the entry sequence so they can all be cancelled on cleanup.
    const clearTimersRef = useRef([])
    // Always-current data ref so the entry-sequence timer reads entryBytes/settingbytes
    // at fire-time (3 s after connect) rather than at schedule-time when data may still be null.
    const latestDataRef = useRef(null)

    const [data, setData] = useState(dataProp || null)
    const [loading, setLoading] = useState(
        !dataProp && subprogramId ? true : false
    )
    const [outputStates, setOutputStates] = useState({})
    const [outputImages, setOutputImages] = useState({})
    const [aiVoiceover, setAiVoiceover] = useState(
        () => sessionStorage.getItem('aiVoiceToggle') !== 'false'
    )

    const [showSubtitleDisplay, setShowSubtitleDisplay] = useState(false)
    const [subtitleVisible, setSubtitleVisible] = useState(false)
    const [typewriterText, setTypewriterText] = useState('')
    const subtitleRef = useRef(null)
    const [aiSpeaking, setAiSpeaking] = useState(true)

    const [wires, setWires] = useState([])

    const [userInteractedThisStep, setUserInteractedThisStep] = useState(false)

    const [modelOverrides, setModelOverrides] = useState({})

    // Momentary-button gating: after a tack press we wait for the device's
    // "Rdone" response (\r\nRdone\r\n) before allowing another tack/hold press.
    // This stops spamming while the previous action is still running on the
    // hardware. A safety fallback timer releases the lock if that response
    // never arrives (e.g. device unplugged mid-action).
    // Locked while an action is (believed to be) running on the board.
    const awaitingResponseRef = useRef(false)
    // Safety-net timer, re-armed on every "busy" reply and cleared by "Rdone".
    const releaseTimerRef = useRef(null)
    // Timestamp of the last tack press, used to decide whether a "busy" reply
    // is a response to our command (→ re-lock) or unrelated chatter.
    const lastPressAtRef = useRef(0)
    // Mapping (id) of the tack button currently mid-action, so it can be
    // returned to its OFF state once the action completes.
    const activeTackMappingRef = useRef(null)
    const noticeTimerRef = useRef(null)
    // Text of the bottom notice ('' = hidden). Reused for the "please wait" and
    // "please connect the device" messages.
    const [noticeText, setNoticeText] = useState('')

    // --- NEW: Track if the screen is in portrait mode ---
    // const [isPortrait, setIsPortrait] = useState(
    //     window.innerHeight * 1.5 >= window.innerWidth
    // )

    // --- NEW: Check User Agent for mobile and track portrait mode ---
    const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )

    const [isMobilePortrait, setIsMobilePortrait] = useState(
        isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
    )

    // ===== STEP STATE =====
    const storageKey = `playScreen_step_${projectId}_${screenId || 'default'}`
    const hasSteps = data?.steps && data.steps.length > 0
    const totalSteps = data?.totalSteps || data?.steps?.length || 0

    // Read from sessionStorage on initial load
    const [currentStep, setCurrentStep] = useState(() => {
        if (isPreviewMode) return 1

        // PRIORITY 1: Explicit jump from Timeline
        if (initialStep) {
            sessionStorage.setItem(storageKey, initialStep.toString())
            return initialStep
        }

        // PRIORITY 2: Memory from last visit
        const savedStep = sessionStorage.getItem(storageKey)
        const parsed = parseInt(savedStep, 10)
        return !isNaN(parsed) && parsed > 0 ? parsed : 1
    })

    // Save step to storage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(storageKey, currentStep)
    }, [currentStep, storageKey])

    // Kit Menu play has no final step: if a stale saved step points past the
    // last real step, clamp back so the Congratulations screen never appears.
    useEffect(() => {
        if (hideFinalStep && hasSteps && currentStep > totalSteps) {
            setCurrentStep(totalSteps)
        }
    }, [hideFinalStep, hasSteps, currentStep, totalSteps])

    const currentStepData = useMemo(() => {
        if (!hasSteps) return null

        // Handle the injected Final Step
        if (currentStep > totalSteps) {
            // Kit Menu play skips the "Congratulations" step — stay on the last
            // real step instead of showing the injected completion screen.
            if (hideFinalStep) {
                return (
                    data.steps.find((s) => s.stepNumber === totalSteps) || null
                )
            }
            return {
                title: data.finalStepTitle || 'Tutorial Complete!',
                instruction:
                    data.finalStepInstruction ||
                    'Great job! Click Continue to proceed.',
                validations: [], // No validations needed to pass final step
                visibleComponents:
                    data.steps[totalSteps - 1]?.visibleComponents || [],
                enableComponents:
                    data.steps[totalSteps - 1]?.enableComponents || [],
                highlight: [], // Remove all highlights
            }
        }

        return data.steps.find((s) => s.stepNumber === currentStep) || null
    }, [hasSteps, data, currentStep, totalSteps, hideFinalStep])

    const visibleComponentIds = useMemo(() => {
        if (!hasSteps || !currentStepData) return null
        return new Set(currentStepData.visibleComponents || [])
    }, [hasSteps, currentStepData])

    const enabledComponentIds = useMemo(() => {
        if (!hasSteps || !currentStepData) return null
        return new Set(currentStepData.enableComponents || [])
    }, [hasSteps, currentStepData])

    const highlightedComponentIds = useMemo(() => {
        if (!hasSteps || !currentStepData) return new Set()
        return new Set(currentStepData.highlight || [])
    }, [hasSteps, currentStepData])

    // const NO_WIRE_COMPONENT_IDS = useMemo(() => new Set(['led_output_1']), [])

    const currentInstruction = useMemo(() => {
        if (!hasSteps) return data?.description || ''
        return currentStepData?.instruction || ''
    }, [hasSteps, currentStepData, data])

    // ===== AUDIO =====
    const playAudioForStep = useCallback(
        (stepNumber) => {
            if (!data?.meta?.audioBaseUrl) return
            const audioUrl = `${data.meta.audioBaseUrl}${stepNumber}.mp3?t=${Date.now()}`

            // Make mode: play through the shell's persistent, pre-unlocked
            // audio element so playback survives cross-screen navigation.
            if (makeCtx?.playAudio) {
                makeCtx.playAudio(audioUrl)
                return
            }

            if (!audioRef.current) return
            audioRef.current.src = audioUrl
            audioRef.current
                .play()
                .catch((e) => console.log('Playback interrupted:', e))
        },
        [data, makeCtx]
    )

    const handleAiVoiceoverToggle = useCallback(() => {
        const nextState = !aiVoiceover
        setAiVoiceover(nextState)
        sessionStorage.setItem('aiVoiceToggle', String(nextState))

        if (nextState) {
            playAudioForStep(hasSteps ? currentStep : 1)
            setShowSubtitleDisplay(true)
        } else {
            if (makeCtx?.stopAudio) {
                makeCtx.stopAudio()
            } else if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.currentTime = 0
            }
        }
    }, [aiVoiceover, playAudioForStep, hasSteps, currentStep, makeCtx])

    // Load data from backend if in confined play mode
    useEffect(() => {
        // --- NEW: Resize listener to check aspect ratio dynamically ---
        const handleResize = () => {
            setIsMobilePortrait(
                isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
            )
        }
        window.addEventListener('resize', handleResize)

        if (!dataProp && subprogramId && isConfinedPlay) {
            const confinedPlayScreenId = sessionStorage.getItem('playScreenId')
            let url = `${BACKEND_URL}/api/play/${subprogramId}`
            if (confinedPlayScreenId) url += `/${confinedPlayScreenId}`
            fetch(url)
                .then((res) => res.json())
                .then((playData) => {
                    setData(playData)
                    setLoading(false)
                })
                .catch((err) => {
                    console.error(
                        'Failed to load play data for subprogram:',
                        err
                    )
                    setLoading(false)
                })
        }

        return () => {
            // --- NEW: Cleanup resize listener ---
            window.removeEventListener('resize', handleResize)
        }
    }, [subprogramId, isConfinedPlay, dataProp])

    // Cleanup: clear confined play flags when component unmounts
    useEffect(() => {
        return () => {
            // Always clear the hide-final-step flag so it never leaks into a
            // later PlayScreen opened by a different route (preview, PlayMode…).
            sessionStorage.removeItem('playHideFinalStep')
            if (isConfinedPlay) {
                sessionStorage.removeItem('isConfinedPlay')
                sessionStorage.removeItem('playSubprogramId')
                sessionStorage.removeItem('playScreenId')
            }
        }
    }, [])

    // Keep latestDataRef in sync so the entry-sequence timer always reads the
    // most-recent data even though 'data' is not in the effect's dep array.
    useEffect(() => { latestDataRef.current = data }, [data])

    // ===== SEND ENTRY SEQUENCE ON DEVICE CONNECT =====
    // Order: WOOF → (wait for Rdone OR 2 s fallback) → entryBytes → 300 ms → settingbytes
    // Using Rdone as the gate means entry bytes only go out after the device has
    // finished processing WOOF, eliminating the "busy" rejection entirely.
    const hasSentPlayBytesRef = useRef(false)

    useEffect(() => {
        if (!worker || !webSerial?.isConnected || hasSentPlayBytesRef.current) {
            if (!webSerial?.isConnected) hasSentPlayBytesRef.current = false
            return
        }

        hasSentPlayBytesRef.current = true

        const sendPostWoofSequence = () => {
            const d = latestDataRef.current

            // entryBytes immediately after Rdone (or fallback)
            if (d?.entryBytesEnabled && d?.entryBytes && d.entryBytes.length > 0) {
                const tokens = String(d.entryBytes).match(/\d+|\D/g) || []
                const entryByteArr = tokens.map((tok) =>
                    /^\d+$/.test(tok) ? Math.min(255, parseInt(tok, 10)) : tok.charCodeAt(0)
                )
                if (entryByteArr.length > 0) {
                    worker.postMessage({ type: 'writeArray', value: entryByteArr })
                    console.log('[PlayScreen] entryBytes sent:', entryByteArr)
                }
            }

            // settingbytes (LS) 150 ms after entry bytes
            if (d?.settingbytes && Array.isArray(d.settingbytes) && d.settingbytes.length > 0) {
                const t = setTimeout(() => {
                    worker.postMessage({ type: 'writeArray', value: d.settingbytes })
                    console.log('[PlayScreen] settingbytes sent:', d.settingbytes)
                }, 150)
                clearTimersRef.current.push(t)
            }
        }

        const t1 = setTimeout(() => {
            const woofSentAt = Date.now()
            worker.postMessage({ type: 'writeArray', value: [87, 79, 79, 70] }) // WOOF
            console.log('[PlayScreen] WOOF sent')

            let done = false

            // Fallback: if the device never sends Rdone within 1.5 s, proceed anyway
            const fallback = setTimeout(() => {
                if (done) return
                done = true
                worker.removeEventListener('message', rdoneListener)
                console.log('[PlayScreen] Rdone not received — fallback after 1.5 s')
                sendPostWoofSequence()
            }, 1500)
            clearTimersRef.current.push(fallback)

            // Primary: wait for device Rdone AFTER our WOOF (ignore stale Rdone
            // from previous screens by requiring ≥200 ms since we sent WOOF)
            const rdoneListener = (e) => {
                if (done) return
                if (Date.now() - woofSentAt < 200) return // stale — not from our WOOF
                const msg = e.data || {}
                let str = ''
                if (msg.type === 'read') str = msg.value
                else if (msg.type === 'actionRead') str = msg.value?.stringData
                if (typeof str !== 'string' || !str.includes('Rdone')) return
                done = true
                clearTimeout(fallback)
                worker.removeEventListener('message', rdoneListener)
                console.log('[PlayScreen] Rdone received — sending post-WOOF sequence')
                sendPostWoofSequence()
            }
            worker.addEventListener('message', rdoneListener)
        }, 1500)

        clearTimersRef.current.push(t1)

        return () => {
            clearTimersRef.current.forEach(clearTimeout)
            clearTimersRef.current = []
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [worker, webSerial?.isConnected])

    const buildStateUpToStep = (targetStepNumber, allSteps) => {
        let accumulatedState = {}

        // Helper: get all conditions from a step regardless of schema
        const getStepConds = (s) =>
            s.conditionBlocks ? s.conditionBlocks.flat() : s.validations || []

        // PRE-FILL: Scan all steps and initialize every known key to 0.
        // Supports conditionBlocks (new) and validations (legacy).
        allSteps.forEach((step) => {
            getStepConds(step).forEach((cond) => {
                const key = cond.inputId || cond.mapping
                if (key && accumulatedState[key] === undefined) {
                    accumulatedState[key] = 0
                }
            })
        })

        // Loop through all steps up to the target step to build the continuous state
        for (let i = 0; i < targetStepNumber; i++) {
            const step = allSteps[i]
            if (!step) continue
            const conds = getStepConds(step)
            if (!conds.length) continue

            const stepState = {}
            const ranges = {}

            const MOMENTARY_BUTTON_TYPES = [
                'tack_button_control',
                'hold_button_control',
                'trigger_button_control',
                'ack_button_control',
                'spam_button_control',
                'press_release_button_control',
            ]

            conds.forEach((cond) => {
                const key = cond.inputId || cond.mapping
                if (!key) return

                // Momentary buttons must never have ON state persisted across
                // steps – they are press-and-hold inputs, not toggles.
                const condType = cond.inputType || cond.type
                if (
                    MOMENTARY_BUTTON_TYPES.includes(condType) ||
                    MOMENTARY_BUTTON_TYPES.some((t) => key.startsWith(t))
                )
                    return

                // NEW schema: has inputId
                if (cond.inputId) {
                    if (cond.expectedStatus !== undefined) {
                        // Button-type condition
                        stepState[key] = cond.expectedStatus === 'ON' ? 100 : 0
                    } else {
                        // Slider / RGB channel condition
                        const { operator, value } = cond
                        if (operator === '==') {
                            stepState[key] = value
                        } else if (operator === '>') {
                            if (value === 0) stepState[key] = 100
                            else
                                ranges[key] = {
                                    ...ranges[key],
                                    min: value + 1,
                                }
                        } else if (operator === '>=') {
                            ranges[key] = { ...ranges[key], min: value }
                        } else if (operator === '<') {
                            ranges[key] = { ...ranges[key], max: value - 1 }
                        } else if (operator === '<=') {
                            ranges[key] = { ...ranges[key], max: value }
                        }
                    }
                    return
                }

                // LEGACY schema: has mapping
                const { operator, value } = cond
                if (operator === '==') {
                    stepState[key] = value
                } else if (operator === '>') {
                    if (value === 0) stepState[key] = 100
                    else ranges[key] = { ...ranges[key], min: value + 1 }
                } else if (operator === '>=') {
                    ranges[key] = { ...ranges[key], min: value }
                } else if (operator === '<') {
                    ranges[key] = { ...ranges[key], max: value - 1 }
                } else if (operator === '<=') {
                    ranges[key] = { ...ranges[key], max: value }
                }
            })

            // Resolve any ranges (like >= 30 and <= 70) to their midpoint
            Object.keys(ranges).forEach((key) => {
                if (stepState[key] !== undefined) return
                const r = ranges[key]
                if (r.min !== undefined && r.max !== undefined) {
                    stepState[key] = Math.floor((r.min + r.max) / 2)
                } else if (r.min !== undefined) {
                    stepState[key] = r.min
                } else if (r.max !== undefined) {
                    stepState[key] = r.max
                }
            })

            accumulatedState = { ...accumulatedState, ...stepState }
        }
        return accumulatedState
    }

    // ===== STEP NAVIGATION =====
    const handleStepNext = useCallback(() => {
        setUserInteractedThisStep(false) // Reset lock

        if (!hasSteps) {
            if (dataProp && !isConfinedPlay) ContentEngine.next()
            return
        }

        if (currentStep <= totalSteps) {
            // Fire hardware bytes for this step's onSuccessActions
            if (worker) {
                ; (currentStepData?.onSuccessActions || []).forEach(
                    async (action) => {
                        // free===true → fixedBytes (recorded), free===false → direct (raw)
                        // Also support legacy field names (savedBytes/rawBytes) for old data
                        const fixedBytes =
                            action.fixedBytes || action.savedBytes || null
                        const directBytes =
                            action.direct || action.rawBytes || null
                        const useFree =
                            action.free !== undefined
                                ? action.free
                                : fixedBytes !== null

                        if (
                            useFree &&
                            fixedBytes &&
                            Array.isArray(fixedBytes)
                        ) {
                            for (const bytes of fixedBytes) {
                                worker.postMessage({
                                    type: 'writeArray',
                                    value: bytes,
                                })
                                await new Promise((r) => setTimeout(r, 100))
                            }
                        } else if (!useFree && directBytes) {
                            if (Array.isArray(directBytes)) {
                                if (
                                    directBytes.length > 0 &&
                                    Array.isArray(directBytes[0])
                                ) {
                                    // New 2D format: iterate packets
                                    for (const packet of directBytes) {
                                        worker.postMessage({
                                            type: 'writeArray',
                                            value: packet,
                                        })
                                        await new Promise((r) =>
                                            setTimeout(r, 100)
                                        )
                                    }
                                } else {
                                    // Legacy 1D format: send as single packet
                                    worker.postMessage({
                                        type: 'writeArray',
                                        value: directBytes,
                                    })
                                }
                            } else if (typeof directBytes === 'string') {
                                // Legacy string format
                                worker.postMessage({
                                    type: 'write',
                                    value: directBytes,
                                })
                            }
                        }
                    }
                )
            }
            const nextStep = currentStep + 1
            // Kit Menu play: the last real step is the end — don't advance into
            // the injected "Congratulations" step.
            if (hideFinalStep && nextStep > totalSteps) {
                setShowSubtitleDisplay(true)
                return
            }
            // Earn the current step globally
            const globalStep = ContentEngine.getCurrentScreenOffset() + currentStep
            ContentEngine.tryEarnStep(globalStep)
            setCurrentStep(nextStep)
            if (aiVoiceover) playAudioForStep(nextStep)
        } else {
            if (dataProp && !isConfinedPlay) {
                sessionStorage.removeItem(storageKey)
                ContentEngine.next()
            }
        }
        setShowSubtitleDisplay(true)
    }, [
        hasSteps,
        currentStep,
        totalSteps,
        aiVoiceover,
        playAudioForStep,
        dataProp,
        isConfinedPlay,
        hideFinalStep,
        worker,
        currentStepData,
    ])

    const handleStepPrev = useCallback(() => {
        setUserInteractedThisStep(false)

        if (!hasSteps) {
            if (dataProp && !isConfinedPlay) ContentEngine.prev()
            return
        }

        if (currentStep > 1) {
            const prevStep = currentStep - 1
            const targetStateStep =
                prevStep > totalSteps ? totalSteps - 1 : prevStep - 1
            setOutputStates(buildStateUpToStep(targetStateStep, data.steps))
            setCurrentStep(prevStep)
            if (aiVoiceover) playAudioForStep(prevStep)
        } else if (dataProp && !isConfinedPlay) {
            sessionStorage.removeItem(storageKey)
            ContentEngine.prev()
        }
        setShowSubtitleDisplay(true)
    }, [
        hasSteps,
        currentStep,
        aiVoiceover,
        playAudioForStep,
        dataProp,
        data,
        isConfinedPlay,
    ])

    const resetTutorial = useCallback(() => {
        setUserInteractedThisStep(false)
        setCurrentStep(1)

        setOutputStates(buildStateUpToStep(0, data?.steps || []))

        if (aiVoiceover) playAudioForStep(1)
        setShowSubtitleDisplay(true)
    }, [aiVoiceover, playAudioForStep, data])

    // ===== VALIDATION LOGIC =====
    const evaluateCondition = useCallback(
        (cond) => {
            // NEW schema: uses inputId
            if (cond.inputId) {
                const BUTTON_TYPES = [
                    'button_control',
                    'tack_button_control',
                    'hold_button_control',
                    'trigger_button_control',
                    'rgb_button_control',
                    'ack_button_control',
                    'spam_button_control',
                    'press_release_button_control',
                ]

                if (BUTTON_TYPES.includes(cond.inputType)) {
                    const val = outputStates[cond.inputId] || 0
                    const status = val > 0 ? 'ON' : 'OFF'
                    return status === (cond.expectedStatus || 'ON')
                }

                if (cond.inputType === 'rgb_control' && cond.channel) {
                    const channelKey = `${cond.inputId}_${cond.channel}`
                    const channelVal = Number(outputStates[channelKey]) || 0
                    return applyNumericOperator(
                        channelVal,
                        cond.operator || '>',
                        cond.value
                    )
                }

                if (cond.inputType === 'slider_control') {
                    const val = Number(outputStates[cond.inputId]) || 0
                    return applyNumericOperator(
                        val,
                        cond.operator || '>',
                        cond.value
                    )
                }

                return false
            }

            // LEGACY schema: uses mapping string
            if (cond.mapping !== undefined) {
                const currentValue = Number(outputStates[cond.mapping]) || 0
                return applyNumericOperator(
                    currentValue,
                    cond.operator || '>',
                    cond.value
                )
            }

            return false
        },
        [outputStates]
    )

    const isStepValid = useMemo(() => {
        if (!hasSteps || !currentStepData) return true
        // Free step: always valid, user just clicks Next
        if (currentStepData.isFreeStep) return true
        // New schema: conditionBlocks (outer OR, inner AND)
        if (currentStepData.conditionBlocks) {
            if (!currentStepData.conditionBlocks.length) return true
            return currentStepData.conditionBlocks.some((block) =>
                block.every((cond) => evaluateCondition(cond))
            )
        }
        // Legacy schema: flat AND validations
        if (!currentStepData.validations) return true
        return currentStepData.validations.every((cond) =>
            evaluateCondition(cond)
        )
    }, [hasSteps, currentStepData, evaluateCondition])

    // ===== AUTO ADVANCE STEP =====
    useEffect(() => {
        if (!hasSteps || currentStep > totalSteps) return

        // ADD THIS LINE: Prevent auto-advance if it's a free step so they MUST click next
        if (currentStepData?.isFreeStep) return

        // Only auto-advance when there are conditions to evaluate
        const hasConditions =
            currentStepData?.conditionBlocks?.length ||
            currentStepData?.validations?.length
        if (!hasConditions) return

        // The lock: Only auto-advance if the step is valid AND the user actually did something
        if (isStepValid && userInteractedThisStep) {
            const timer = setTimeout(() => {
                handleStepNext()
            }, 200) // so that user can keep the tack button pressed
            return () => clearTimeout(timer)
        }
    }, [
        isStepValid,
        userInteractedThisStep,
        currentStepData,
        hasSteps,
        currentStep,
        totalSteps,
        handleStepNext,
    ])

    // ===== INITIAL LOAD & RESTORE STATE =====
    useEffect(() => {
        if (!loading && data) {
            // Restore interactive states (sliders/buttons) if we reloaded mid-tutorial
            if (currentStep > 1 && data.steps) {
                setOutputStates(buildStateUpToStep(currentStep - 1, data.steps))
            }

            // Seed every output with its defaultImage so the screen starts correctly.
            // The global eval will only override these when an action explicitly fires.
            const seeded = {}
                ; (data.components || []).forEach((comp) => {
                    if (comp.type === 'output' || comp.type === 'custom_output') {
                        seeded[comp.id] = comp.properties?.defaultImage || ''
                    }
                })
            setOutputImages(seeded)

            // Play audio for the saved step (instead of hardcoded 1)
            if (aiVoiceover) {
                playAudioForStep(currentStep)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data]) // only on initial load

    // ===== GLOBAL OUTPUT IMAGE EVALUATION =====
    // Runs whenever ANY input state changes. Evaluates ALL inputs' events/conditions
    // with AND gate (requiredConditions), then rebuilds outputImages using specificity
    // (weight = number of requiredConditions). Higher weight wins per output.
    useEffect(() => {
        if (!data?.components) return

        const TOGGLE_BUTTON_TYPES = [
            'button_control',
            'tack_button_control',
            'hold_button_control',
            'rgb_button_control',
            'ack_button_control',
            'spam_button_control',
            'press_release_button_control',
        ]
        const TRIGGER_TYPES = ['trigger_button_control']
        const SLIDER_TYPES = ['slider_control', 'rgb_control']

        // outputCandidates tracks the highest-weight winning image per output:
        // { [outputId]: { image, weight } }
        const outputCandidates = {}

        // Evaluate an action's requiredConditions; if met, update the candidate for
        // its outputId only if the new weight beats the current winner.
        // isInactiveFallback=true (onOFF when val=0) forces weight=-1 so any active
        // action with weight>=0 will always override it.
        const applyAction = (action, isInactiveFallback = false) => {
            if (!action.outputId || !action.targetImage) return
            const reqs = action.requiredConditions || []
            if (!reqs.every((req) => evaluateCondition(req))) return
            const weight = isInactiveFallback ? -1 : (action.weight ?? 0)
            const current = outputCandidates[action.outputId]
            if (!current || weight > current.weight) {
                outputCandidates[action.outputId] = {
                    image: action.targetImage,
                    weight,
                }
            }
        }

        data.components.forEach((inputComp) => {
            const { type, properties = {}, id } = inputComp
            const events = properties.events || {}

            if (TOGGLE_BUTTON_TYPES.includes(type)) {
                const val = outputStates[id] || 0
                if (val > 0) {
                    ; (events.onON || []).forEach((action) =>
                        applyAction(action, false)
                    )
                } else {
                    ; (events.onOFF || []).forEach((action) =>
                        applyAction(action, true)
                    )
                }
            } else if (TRIGGER_TYPES.includes(type)) {
                const val = outputStates[id] || 0
                if (val > 0) {
                    ; (events.onTrigger || []).forEach((action) =>
                        applyAction(action, false)
                    )
                }
            } else if (SLIDER_TYPES.includes(type)) {
                ; (properties.conditions || []).forEach((cond) => {
                    let matches = false
                    const channelVal =
                        type === 'rgb_control' && cond.channel
                            ? Number(outputStates[`${id}_${cond.channel}`]) || 0
                            : Number(outputStates[id]) || 0
                    if (cond.type === 'range') {
                        matches =
                            channelVal >= (cond.min ?? 0) &&
                            channelVal <= (cond.max ?? 100)
                    } else {
                        matches = applyNumericOperator(
                            channelVal,
                            cond.operator || '>',
                            cond.value ?? 0
                        )
                    }
                    if (matches) {
                        ; (cond.actions || []).forEach((action) =>
                            applyAction(action, false)
                        )
                    }
                })
            }
        })

        // Extract winning images from candidates.
        // Only update outputs that have an explicit action firing — outputs with no
        // matching action keep their current image (no auto-revert to defaultImage).
        const explicitImages = {}
        Object.entries(outputCandidates).forEach(([outputId, candidate]) => {
            explicitImages[outputId] = candidate.image
        }) // These form the baseline state of the board.

        // Collect onSuccessActions from all steps that have already been passed.
        // These form the baseline state of the board.
        const stepSuccessImages = {}
        if (hasSteps && data.steps) {
            data.steps.forEach((step) => {
                if (step.stepNumber < currentStep) {
                    ; (step.onSuccessActions || []).forEach((a) => {
                        if (a.outputId && a.targetImage) {
                            stepSuccessImages[a.outputId] = a.targetImage
                        }
                    })
                }
            })
        }

        // Priority (lowest → highest):
        //   component interaction images → past-step success images
        // Past-step targetImages must win so that the image set by completing step N
        // persists and is visible when the user enters step N+1, regardless of what
        // the component events (e.g. onOFF) would otherwise show.
        const combined = {
            ...explicitImages,
            ...stepSuccessImages,
        }
        if (Object.keys(combined).length > 0) {
            setOutputImages((prev) => ({ ...prev, ...combined }))
        }
    }, [outputStates, data, evaluateCondition, currentStep, hasSteps])

    // ===== DATA FETCHING =====
    useEffect(() => {
        if (dataProp) {
            setData(dataProp)
            setLoading(false)
            return
        }

        // --- NEW: Intercept unsaved preview data ---
        // Always check sessionStorage first when in preview mode — this handles
        // migrated data previews where the admin stores formData before navigating.
        if (isPreviewMode) {
            const tempData = sessionStorage.getItem('tempPreviewData')
            if (tempData) {
                console.log('Loading unsaved preview data from session storage')
                setData(JSON.parse(tempData))
                setLoading(false)
                return // Exit early so we don't fetch from the backend!
            }
        }

        if (!projectId) {
            setLoading(false)
            return
        }

        async function fetchData() {
            try {
                setLoading(true)

                // Dynamically select Staging or Production API
                let endpoint = isPreviewMode
                    ? `${BACKEND_URL}/api/staging/play/${projectId}`
                    : `${BACKEND_URL}/api/play/${projectId}`
                if (screenId) endpoint += `/${screenId}`

                const res = isPreviewMode
                    ? await fetch(endpoint, { credentials: 'include' })
                    : await fetch(endpoint)
                if (!res.ok) throw new Error('Failed to fetch play screen')
                const json = await res.json()
                setData(json)
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [dataProp, projectId, isPreviewMode])

    // ===== WIRES =====
    const calculateWires = useCallback(() => {
        if (!data || !data.components || !safeAreaRef.current) return

        const containerRect = safeAreaRef.current.getBoundingClientRect()
        const boardComp = data.components.find(
            (c) => c.type === 'toki_board' || c.type === 'board'
        )
        const device = sessionStorage.getItem('allowedDevice') || 'Toki'
        if (!boardComp) return

        const boardEl = document.getElementById(boardComp.id)
        if (!boardEl) return
        const boardRect = boardEl.getBoundingClientRect()

        // --- NEW: Calculate how much the board has scaled ---
        // (300 is the approximate original pixel width of your board image)
        const BASE_BOARD_WIDTH = device === 'Poki' ? 150 : 290 // Adjust if needed
        const boardScale = boardRect.width / BASE_BOARD_WIDTH

        const tokiPortOffsets = {
            M1: [152, 112],
            M3: [178, 112],
            servo_motor1: [147, 71],
            servo_motor2: [183, 71],
            A1: [147, 90],
            A2: [183, 90],
        }

        const pokiPortOffsets = {
            servo_motor1: [33, 7],
            servo_motor2: [115, 7],

            A1: [29, 18],
            A2: [121, 18],

            B1: [33, 77],
            B2: [115, 46],

            C1: [33, 60],
            C2: [33, 68],

            D1: [115, 62],
            D2: [115, 54],

            E: [6, 42],
            E1: [18, 42],
            E2: [29, 42],

            F: [117, 70],
            F1: [131, 70],
            F2: [142, 70],

            M1: [43, 85],
            M3: [105, 85],
        }

        const COMPONENT_OFFSET = [25, 81]
        const newWires = []

        // Only draw wires from board to output / custom_output components
        const shouldWireComponent = (comp) => {
            if (!comp?.port) return false
            if (comp.type !== 'output' && comp.type !== 'custom_output')
                return false

            if (comp.properties?.showWire === true) return true
            if (comp.properties?.showWire === false) return false

            return comp.id !== 'led_output_1'
        }

        const componentsToWire = visibleComponentIds
            ? data.components.filter((c) => visibleComponentIds.has(c.id))
            : data.components

        componentsToWire.forEach((comp) => {
            if (shouldWireComponent(comp)) {
                const compEl = document.getElementById(comp.id)
                if (!compEl) return

                const compRect = compEl.getBoundingClientRect()

                const DISPLAY_TO_INTERNAL_PORT = {
                    M2: 'M3',
                    M4: 'M4',
                    S1: 'servo_motor1',
                    S2: 'servo_motor2',
                }
                const isTokiFamily = ['Toki', 'Poki'].includes(device)
                const internalPort =
                    (isTokiFamily && DISPLAY_TO_INTERNAL_PORT[comp.port])
                        ? DISPLAY_TO_INTERNAL_PORT[comp.port]
                        : comp.port

                let portOffset = [0, 0]
                if (device === 'Toki' && tokiPortOffsets[internalPort]) {
                    portOffset = tokiPortOffsets[internalPort]
                } else if (device === 'Poki' && pokiPortOffsets[internalPort]) {
                    portOffset = pokiPortOffsets[internalPort]
                } else {
                    portOffset = Ports(internalPort) || [0, 0]
                }

                // --- CHANGED: Multiply port offsets by the scale factor ---
                const startX =
                    boardRect.left -
                    containerRect.left +
                    portOffset[0] * boardScale
                const startY =
                    boardRect.top -
                    containerRect.top +
                    portOffset[1] * boardScale
                let endX =
                    compRect.left - containerRect.left + COMPONENT_OFFSET[0]
                let endY =
                    compRect.top - containerRect.top + COMPONENT_OFFSET[1]

                // =========== do some tweaking to endX and endY if dynamex motor
                let imgSrcToRender = resolveStateImage(
                    comp.properties.states,
                    comp.properties.defaultImage
                )
                if (!imgSrcToRender) imgSrcToRender = comp.properties.offImage
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'dynamexMotor@2x.png'
                ) {
                    endX += 10
                    endY -= 16
                }
                // ============= for ultrasonic sensor
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'ultrasonicToki@2x.png'
                ) {
                    endX += 10
                    endY -= 36
                }
                // ============= for dc motor
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'dcmotor.png'
                ) {
                    endY -= 6
                }
                // ============= for OLED
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'tokiOled@2x.png'
                ) {
                    endY -= 38
                    endX += 36
                }
                // ============= for IMU
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'tokiOled@2x.png'
                ) {
                    endY -= 6
                }
                // ============= for color_sensor
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'colorSensor@2x.png'
                ) {
                    endY -= 6
                }
                // ========================================

                const controlPointYOffset = Math.abs(endY - startY) * 0.5
                const cp1X = startX
                const cp1Y = startY + controlPointYOffset + 50
                const cp2X = endX
                const cp2Y = endY + controlPointYOffset + 60

                const pathString = `M ${startX} ${startY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${endX} ${endY}`

                newWires.push({
                    id: `${boardComp.id}-to-${comp.id}`,
                    path: pathString,
                })
            }
        })
        setWires(newWires)
    }, [data, visibleComponentIds]) // Removed NO_WIRE_COMPONENT_IDS from dependencies

    useEffect(() => {
        // Recalculate wires when the window is resized or zoomed
        window.addEventListener('resize', calculateWires)

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener('resize', calculateWires)
        }
    }, [calculateWires])

    // ===== MODEL ANIMATIONS (Input-Driven) =====
    useEffect(() => {
        if (!data?.components) return

        const TOGGLE_BUTTON_TYPES = [
            'button_control',
            'tack_button_control',
            'hold_button_control',
            'rgb_button_control',
            'ack_button_control',
            'spam_button_control',
            'press_release_button_control',
        ]
        const TRIGGER_TYPES = ['trigger_button_control']
        const SLIDER_TYPES = ['slider_control', 'rgb_control']

        const resolveModelDuration = (mv) => {
            const direct = Number(mv?.duration) || 0
            if (direct > 0) return direct
            return Number(mv?.model?.animations?.[0]?.duration) || 0
        }

        const modelComps = data.components.filter((c) => c.type === 'model')
        const getModelComp = (id) => modelComps.find((m) => m.id === id)

        const execModelAction = (ma) => {
            const mv = modelRefs.current[ma.targetModelId]
            if (!mv) return
            const { action } = ma
            if (action === 'switchModelAnim') {
                const mc = getModelComp(ma.targetModelId)
                setModelOverrides((prev) => ({
                    ...prev,
                    [ma.targetModelId]: {
                        modelUrl: ma.targetModelUrl || mc?.modelUrl,
                        animationName:
                            ma.targetAnimation ||
                            currentStepData?.models?.animationName,
                        cameraOrbit: ma.targetOrbit || mc?.cameraOrbit,
                    },
                }))
                return
            }
            let duration = resolveModelDuration(mv)
            if (duration <= 0) {
                mv.play?.()
                mv.pause?.()
                duration = resolveModelDuration(mv)
            }
            if (action === 'play') mv.play?.()
            if (action === 'pause') mv.pause?.()
            if (action === 'togglePlay') {
                mv.paused ? mv.play?.() : mv.pause?.()
            }
            if (action === 'jumpTo' && duration > 0) {
                mv.pause?.()
                mv.currentTime = (ma.targetFrame ?? 0) * duration
            }
            if (action === 'glideToFrame' && duration > 0) {
                mv.pause?.()
                const startT = (ma.startFrame ?? 0) * duration
                const endT = (ma.endFrame ?? 1) * duration
                const durationMs = Math.max(100, 5000 - (ma.speed ?? 50) * 45)
                const startTime = performance.now()
                if (mv._glideRaf) cancelAnimationFrame(mv._glideRaf)
                const animate = (now) => {
                    const progress = Math.min((now - startTime) / durationMs, 1)
                    mv.currentTime = startT + (endT - startT) * progress
                    if (progress < 1)
                        mv._glideRaf = requestAnimationFrame(animate)
                }
                mv._glideRaf = requestAnimationFrame(animate)
            }
        }

        const revertSwitchModelAnim = (modelId) => {
            setModelOverrides((prev) => {
                const n = { ...prev }
                delete n[modelId]
                return n
            })
        }

        data.components.forEach((inputComp) => {
            const { type, properties = {}, id } = inputComp
            const modelActions = properties.modelActions || {}

            if (TOGGLE_BUTTON_TYPES.includes(type)) {
                const cur = outputStates[id] || 0
                const prev = prevOutputs.current[id] || 0
                if (cur === prev) return
                if (prev === 0 && cur > 0) {
                    ; (modelActions.onON || []).forEach(execModelAction)
                } else if (prev > 0 && cur === 0) {
                    ; (modelActions.onOFF || []).forEach((ma) => {
                        if (ma.action === 'switchModelAnim')
                            revertSwitchModelAnim(ma.targetModelId)
                        else execModelAction(ma)
                    })
                }
            } else if (TRIGGER_TYPES.includes(type)) {
                const cur = outputStates[id] || 0
                const prev = prevOutputs.current[id] || 0
                if (cur === prev) return
                if (prev === 0 && cur > 0) {
                    ; (modelActions.onTrigger || []).forEach(execModelAction)
                }
            } else if (SLIDER_TYPES.includes(type)) {
                const isRgb = type === 'rgb_control'
                    ; (Array.isArray(properties.modelActions)
                        ? properties.modelActions
                        : []
                    ).forEach((ma) => {
                        const stateKey =
                            isRgb && ma.channel ? `${id}_${ma.channel}` : id
                        const cur = Number(outputStates[stateKey]) || 0
                        const prev = Number(prevOutputs.current[stateKey]) || 0
                        if (cur === prev) return

                        const mv = modelRefs.current[ma.targetModelId]
                        if (!mv) return
                        let duration = resolveModelDuration(mv)
                        if (duration <= 0) {
                            mv.play?.()
                            mv.pause?.()
                            duration = resolveModelDuration(mv)
                        }

                        if (ma.type === 'scrub') {
                            mv.pause?.()
                            const linkedSlider = data.components.find(
                                (c) => c.id === id
                            )
                            const minIn = linkedSlider?.properties?.min ?? 0
                            const maxIn = linkedSlider?.properties?.max ?? 100
                            const clamped = Math.max(minIn, Math.min(maxIn, cur))
                            const normalized =
                                maxIn === minIn
                                    ? 0
                                    : (clamped - minIn) / (maxIn - minIn)
                            const startFrame = ma.startFrame ?? 0
                            const endFrame = ma.endFrame ?? 1
                            const targetPercent =
                                startFrame + normalized * (endFrame - startFrame)
                            if (duration > 0)
                                mv.currentTime = targetPercent * duration
                        } else if (ma.type === 'trigger' || !ma.type) {
                            const prevMet = applyNumericOperator(
                                prev,
                                ma.operator || '>',
                                ma.value ?? 0
                            )
                            const nowMet = applyNumericOperator(
                                cur,
                                ma.operator || '>',
                                ma.value ?? 0
                            )
                            if (!prevMet && nowMet) execModelAction(ma)
                        }
                    })
            }
        })

        prevOutputs.current = { ...outputStates }
    }, [outputStates, data])

    // Keep outputStatesRef in sync so the seeding effect below always has a fresh snapshot.
    useEffect(() => {
        outputStatesRef.current = outputStates
    }, [outputStates])

    // ===== SEED prevLiveStatesRef on live step entry =====
    // Without seeding, every live item appears "changed" on the FIRST interaction
    // (prevRef[key] === undefined vs current value), which causes ALL items to send
    // bytes simultaneously — including rgb_control which fires with its stale
    // accumulated value (e.g. 100,100,100) even though only a button was clicked.
    useEffect(() => {
        // Clear the ref whenever we move to a new step regardless of type.
        prevLiveStatesRef.current = {}
        if (!currentStepData?.isLiveStep) return
        const liveItems = normalizeLive(currentStepData?.live)
        if (Object.keys(liveItems).length === 0) return
        // Snapshot current outputStates as the baseline so only real changes fire bytes.
        const snap = outputStatesRef.current
        Object.entries(liveItems).forEach(([inputId]) => {
            const inputComp = data?.components?.find((c) => c.id === inputId)
            if (!inputComp) return
            if (inputComp.type === 'rgb_control') {
                prevLiveStatesRef.current[`${inputId}_red`] =
                    Number(snap[`${inputId}_red`]) || 0
                prevLiveStatesRef.current[`${inputId}_green`] =
                    Number(snap[`${inputId}_green`]) || 0
                prevLiveStatesRef.current[`${inputId}_blue`] =
                    Number(snap[`${inputId}_blue`]) || 0
            } else {
                prevLiveStatesRef.current[inputId] = snap[inputId] || 0
            }
        })
    }, [currentStepData, data])

    // ===== LIVE STEP – send hardware bytes ONLY on input state change =====
    useEffect(() => {
        if (!worker || !webSerial?.isConnected) return
        if (!currentStepData?.isLiveStep) return
        const liveItems = normalizeLive(currentStepData?.live)
        if (Object.keys(liveItems).length === 0) return

        // Helper: transform a raw UI value to its hardware-domain byte value.
        const applyHwTransform = (rawVal, compName) => {
            const ct = compName || ''
            if (ct === 'servo_motor_270') return Math.round(0.6667 * rawVal)
            if (ct === 'servo_motor_360') return 100 + rawVal
            if (
                ct === 'dynamex_motor' ||
                ct === 'dc_motor' ||
                ct === 'geared_motor' ||
                ct === 'mini_geared_motor'
            ) {
                const scale = ct === 'dynamex_motor' ? 0.8 : 1.0
                return Math.round(Math.abs(rawVal) * scale)
            }
            return rawVal
        }

        Object.entries(liveItems).forEach(([inputId, liveItem]) => {
            const {
                index,
                fixedBytes,
                onValue,
                isFree,
                compsIds,
                extraTargets,
            } = liveItem
            if (!fixedBytes || !inputId) return

            const inputComp = data?.components?.find((c) => c.id === inputId)
            if (!inputComp) return

            const inputType = inputComp.type
            let hasChanged = false

            // 1. Check if this specific component's state actually changed
            if (inputType === 'rgb_control') {
                const rKey = `${inputId}_red`
                const gKey = `${inputId}_green`
                const bKey = `${inputId}_blue`

                const rVal = Number(outputStates[rKey]) || 0
                const gVal = Number(outputStates[gKey]) || 0
                const bVal = Number(outputStates[bKey]) || 0

                if (
                    prevLiveStatesRef.current[rKey] !== rVal ||
                    prevLiveStatesRef.current[gKey] !== gVal ||
                    prevLiveStatesRef.current[bKey] !== bVal
                ) {
                    hasChanged = true
                    prevLiveStatesRef.current[rKey] = rVal
                    prevLiveStatesRef.current[gKey] = gVal
                    prevLiveStatesRef.current[bKey] = bVal
                }
            } else {
                const val = outputStates[inputId] || 0
                if (prevLiveStatesRef.current[inputId] !== val) {
                    hasChanged = true
                    prevLiveStatesRef.current[inputId] = val
                }
            }

            // If it didn't change, skip sending bytes so we don't overwrite other active controls!
            if (!hasChanged) return

            // 2. Format and send the bytes
            const bytes = [...fixedBytes]

            if (compsIds) {
                // ── New Architecture ──────────────────────────────────────────
                // isFree=true → send fixedBytes as-is; isFree=false → inject dynamic values.
                if (!isFree) {
                    Object.entries(compsIds).forEach(
                        ([fetchCompId, targetIndices]) => {
                            const fetchComp = data?.components?.find(
                                (c) => c.id === fetchCompId
                            )
                            if (!fetchComp) return

                            const fetchType = fetchComp.type
                            const fetchName =
                                fetchComp.name
                                    ?.trim()
                                    .toLowerCase()
                                    .replace(/ /g, '_') || ''

                            if (fetchType === 'rgb_control') {
                                // Three-channel injection; index -1 means skip that channel.
                                const rVal =
                                    Number(
                                        outputStates[`${fetchCompId}_red`]
                                    ) || 0
                                const gVal =
                                    Number(
                                        outputStates[`${fetchCompId}_green`]
                                    ) || 0
                                const bVal =
                                    Number(
                                        outputStates[`${fetchCompId}_blue`]
                                    ) || 0
                                const channels = [rVal, gVal, bVal]
                                channels.forEach((chVal, chIdx) => {
                                    const byteIdx = targetIndices[chIdx]
                                    if (
                                        byteIdx !== undefined &&
                                        byteIdx !== -1 &&
                                        byteIdx < bytes.length
                                    ) {
                                        bytes[byteIdx] = chVal
                                    }
                                })
                            } else {
                                // Standard component (slider / button).
                                const byteIdx = targetIndices[0]
                                if (
                                    byteIdx === undefined ||
                                    byteIdx === -1 ||
                                    byteIdx >= bytes.length
                                )
                                    return

                                const BUTTON_TYPES = [
                                    'button_control',
                                    'tack_button_control',
                                    'hold_button_control',
                                    'trigger_button_control',
                                    'ack_button_control',
                                    'spam_button_control',
                                    'press_release_button_control',
                                ]
                                if (BUTTON_TYPES.includes(fetchType)) {
                                    const isOn =
                                        (outputStates[fetchCompId] || 0) > 0
                                    const offVal =
                                        fetchName === 'servo_motor_360'
                                            ? 100
                                            : 0
                                    const onVal =
                                        fetchName === 'servo_motor_360'
                                            ? 200
                                            : 100
                                    bytes[byteIdx] = isOn ? onVal : offVal
                                } else {
                                    // slider_control — send raw value; hardware (PWM, motor, etc.) uses its own scale
                                    const rawVal =
                                        Number(outputStates[fetchCompId]) || 0
                                    bytes[byteIdx] = applyHwTransform(rawVal, fetchName)
                                }
                            }
                        }
                    )
                }
                // isFree=true: bytes already contains fixedBytes unchanged.
            } else if (index) {
                // ── Legacy Backward-Compatibility ─────────────────────────────
                if (
                    inputType === 'button_control' ||
                    inputType === 'tack_button_control' ||
                    inputType === 'hold_button_control' ||
                    inputType === 'trigger_button_control' ||
                    inputType === 'ack_button_control' ||
                    inputType === 'spam_button_control' ||
                    inputType === 'press_release_button_control'
                ) {
                    const val = outputStates[inputId] || 0
                    const isOn = val > 0
                    if (typeof onValue === 'object' && onValue !== null) {
                        // RGB button ON/OFF
                        bytes[index[0]] = isOn ? (onValue.r ?? 0) : 0
                        bytes[index[1]] = isOn ? (onValue.g ?? 0) : 0
                        bytes[index[2]] = isOn ? (onValue.b ?? 0) : 0
                    } else {
                        // Standard button ON/OFF
                        const ct = liveItem.compType || ''
                        const offVal = ct === 'servo_motor_360' ? 100 : 0
                        bytes[index[0]] = isOn
                            ? (onValue ?? liveItem.max ?? 100)
                            : offVal
                    }
                } else if (inputType === 'slider_control') {
                    let sliderVal = Number(outputStates[inputId]) || 0
                    const ct = liveItem.compType || ''
                    if (ct === 'servo_motor_270') {
                        sliderVal = Math.round(0.6667 * sliderVal)
                    } else if (ct === 'servo_motor_360') {
                        sliderVal = 100 + sliderVal
                    } else if (
                        ct === 'dynamex_motor' ||
                        ct === 'dc_motor' ||
                        ct === 'geared_motor' ||
                        ct === 'mini_geared_motor'
                    ) {
                        const scale = ct === 'dynamex_motor' ? 0.8 : 1.0
                        sliderVal = Math.round(Math.abs(sliderVal) * scale)
                    }
                    bytes[index[0]] = sliderVal


                } else if (inputType === 'rgb_control') {
                    bytes[index[0]] =
                        Number(outputStates[`${inputId}_red`]) || 0
                    bytes[index[1]] =
                        Number(outputStates[`${inputId}_green`]) || 0
                    bytes[index[2]] =
                        Number(outputStates[`${inputId}_blue`]) || 0
                } else {
                    return
                }
            } else {
                return
            }

            worker.postMessage({ type: 'writeArray', value: bytes })

            // ── Drive any additional target ports from this same input ────────
            // Lets a single input (e.g. one push button) drive several outputs at
            // once, each at its OWN configured value. When the input is active
            // (state != 0, e.g. button pressed) each target fires at its value;
            // when inactive (released / zero) it fires OFF. Non-RGB only (RGB
            // uses an 8-byte packet with a different layout).
            if (
                Array.isArray(extraTargets) &&
                extraTargets.length > 0 &&
                fixedBytes.length !== 8
            ) {
                const inputActive =
                    inputType === 'rgb_control'
                        ? false
                        : (Number(outputStates[inputId]) || 0) !== 0
                extraTargets.forEach((t) => {
                    if (!t) return
                    const portNum =
                        typeof t.portNum === 'number'
                            ? t.portNum
                            : typeof t.port === 'number'
                                ? t.port
                                : null
                    if (portNum === null) return
                    const targetName =
                        t.component?.trim().toLowerCase().replace(/ /g, '_') ||
                        ''
                    // OFF for a 360 servo is neutral (100), everything else is 0.
                    const offVal = targetName === 'servo_motor_360' ? 100 : 0
                    const rawVal = inputActive
                        ? applyHwTransform(Number(t.value) || 0, targetName)
                        : offVal
                    const valueByte = Math.max(
                        0,
                        Math.min(255, Math.round(rawVal))
                    )
                    worker.postMessage({
                        type: 'writeArray',
                        value: [76, 87, t.char ?? 80, portNum, valueByte],
                    })
                })
            }
        })
    }, [outputStates, currentStepData, worker, webSerial, data])

    // ===== TYPEWRITER EFFECT =====
    useEffect(() => {
        if (aiVoiceover && currentInstruction) {
            setTypewriterText('')
            setAiSpeaking(true)
            let i = 0
            const text = currentInstruction
            const interval = setInterval(() => {
                i++
                setTypewriterText(text.slice(0, i))
                if (i >= text.length) {
                    clearInterval(interval)
                    setAiSpeaking(true)
                }
            }, 40)
            return () => clearInterval(interval)
        } else {
            setTypewriterText(currentInstruction || '')
            setAiSpeaking(false)
        }
    }, [aiVoiceover, currentInstruction])

    useEffect(() => {
        const isPlayInteractableClick = (target) => {
            if (!(target instanceof Element)) return false
            return Boolean(
                target.closest(
                    '.playscreen-action-button, .slider-wrapper, .rgb-control-wrapper'
                )
            )
        }

        const handleClick = (e) => {
            // Do not collapse subtitle when user clicks play interactables
            if (isPlayInteractableClick(e.target)) return

            if (
                subtitleRef.current &&
                !subtitleRef.current.contains(e.target)
            ) {
                setShowSubtitleDisplay(false)
                setAiSpeaking(true)
            }
        }

        if (showSubtitleDisplay)
            document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [showSubtitleDisplay])

    // Slide in subtitle on mount
    useEffect(() => {
        const t = setTimeout(() => {
            setSubtitleVisible(true)
            setShowSubtitleDisplay(true)
        }, 50)
        return () => clearTimeout(t)
    }, [])

    useEffect(() => {
        const timer = setTimeout(calculateWires, 200)
        return () => clearTimeout(timer)
    }, [currentStep, calculateWires])

    const goBack = useCallback(() => history.goBack(), [history])

    const handleFullscreen = useCallback(() => {
        // In Make mode, fullscreen the parent wrapper so it persists across screen transitions
        const el = isMake
            ? document.querySelector('.make-page')
            : appContainerRef.current
        const isFullscreen =
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.msFullscreenElement
        if (!isFullscreen) {
            if (el?.requestFullscreen) el.requestFullscreen()
            else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen()
            else if (el?.msRequestFullscreen) el.msRequestFullscreen()
        } else {
            if (document.exitFullscreen) document.exitFullscreen()
            else if (document.webkitExitFullscreen)
                document.webkitExitFullscreen()
            else if (document.msExitFullscreen) document.msExitFullscreen()
        }
    }, [isMake])

    const resolveImage = (imgValue) => {
        if (!imgValue)
            return 'https://cdn-icons-png.flaticon.com/512/863/863268.png'

        if (typeof imgValue === 'string') {
            // Regex updated to handle BOTH single and double quotes: /['"]([^'"]+)['"]/

            // 1. Handle Legacy Hardcoded Project Images
            if (imgValue.includes('renderPrgImage')) {
                const match = imgValue.match(/['"]([^'"]+)['"]/)
                console.log(
                    'Resolving renderPrgImage for:',
                    imgValue,
                    '=>',
                    match ? match[1] : 'No match'
                )
                return match ? renderPrgImage(match[1]) : imgValue
            } else if (imgValue.includes('renderImage')) {
                const match = imgValue.match(/['"]([^'"]+)['"]/)
                return match ? renderImage(match[1]) : imgValue
            } else if (imgValue.includes('renderCompImg')) {
                const match = imgValue.match(/['"]([^'"]+)['"]/)
                return match ? renderCompImg(match[1]) : imgValue
            }
            // 2. Handle External URLs or Base64 Data
            else if (
                imgValue.startsWith('http') ||
                imgValue.startsWith('data:') ||
                imgValue.startsWith('blob:')
            ) {
                return imgValue
            }
            // 3. Handle Backend Uploaded Images
            else if (!imgValue.startsWith('/')) {
                try {
                    return getBackendImage(imgValue)
                } catch (e) {
                    console.error('Failed to load backend image:', e)
                    return `${BACKEND_URL}/static/images/${imgValue}`
                }
            }
        }

        return imgValue
    }

    const resolveModelUrl = (url) => {
        if (!url) return ''
        let finalUrl = url
        const metaModel = Array.isArray(data?.meta?.modelPath)
            ? data.meta.modelPath.find(
                (entry) => typeof entry === 'string' && entry.trim()
            ) || ''
            : data?.meta?.modelPath || ''

        // 1. If it's already a full web URL or data blob, return it directly
        if (
            url.startsWith('http') ||
            url.startsWith('data:') ||
            url.startsWith('blob:')
        ) {
            finalUrl = url
        }
        // 2. If it's a legacy relative path (e.g. /models/...) and we have a GCS URL in meta, prefer that
        else if (url.startsWith('/models/') || !url.startsWith('http')) {
            if (metaModel && metaModel.startsWith('http')) {
                // Use the GCS URL from project_map instead of the stale local path
                finalUrl = metaModel
            } else if (url.startsWith('/models/')) {
                finalUrl = `${BACKEND_URL}/static${url}`
            } else {
                finalUrl = `${BACKEND_URL}/static/models/${url}`
            }
        }

        console.log(`[PlayScreen] Resolved Model URL:`, finalUrl)
        return finalUrl
    }

    // Gate for momentary (tack / hold) button presses. A tack press starts
    // waiting for the device's "Rdone" response; any tack/hold press while we
    // are still waiting is rejected and a "please wait" notice is shown.
    // Returns true if the press is allowed.
    // Briefly surface the bottom notice with the given text (auto-hides).
    const showNotice = useCallback((text) => {
        setNoticeText(text)
        if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
        noticeTimerRef.current = setTimeout(() => setNoticeText(''), 2000)
    }, [])

    // Unlock the momentary buttons and return the running tack button to its OFF
    // state. Called as soon as the board reports the action finished.
    const releaseMomentaryLock = useCallback((reason) => {
        console.log(`[TACK-GATE] RELEASED (${reason}) — buttons clickable again`)
        if (releaseTimerRef.current) {
            clearTimeout(releaseTimerRef.current)
            releaseTimerRef.current = null
        }
        awaitingResponseRef.current = false
        setNoticeText('')
        if (activeTackMappingRef.current != null) {
            const mapping = activeTackMappingRef.current
            activeTackMappingRef.current = null
            setOutputStates((prev) => ({ ...prev, [mapping]: 0 }))
        }
    }, [])

    // Lock the momentary buttons and (re)arm the safety net. Called on a tack
    // press and re-armed on every "busy" reply, so a board that never sends
    // "Rdone" cannot wedge the buttons.
    const holdMomentaryLock = useCallback(() => {
        awaitingResponseRef.current = true
        if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current)
        releaseTimerRef.current = setTimeout(
            () => releaseMomentaryLock('fallback'),
            ACTION_FALLBACK_MS
        )
    }, [releaseMomentaryLock])

    // Gate for momentary (tack / hold / ack) button presses. While the board is
    // running an action, presses are rejected and a "please wait" notice shows.
    // bypassLock=true (Priority / Bypass-Lock designation) skips the awaiting
    // check entirely — the button fires even if a PM-4 action lock is active.
    const requestMomentaryPress = useCallback(
        (kind, mapping, isStop, bypassLock = false) => {
            // A device must be connected before a tack/hold action can run.
            if (!webSerial?.isConnected) {
                console.log(`[TACK-GATE] ${kind} press ignored — not connected`)
                showNotice('Please connect the device first')
                return false
            }
            if (isStop) {
                releaseMomentaryLock('stop press')
                return true
            }
            // Priority buttons bypass the PM-4 action lock completely.
            if (!bypassLock && awaitingResponseRef.current) {
                console.log(`[TACK-GATE] BLOCKED ${kind} press — action running`)
                showNotice('Please wait for the action to complete')
                return false
            }
            if (kind === 'tack') {
                console.log('[TACK-GATE] ARMED on tack press')
                lastPressAtRef.current = Date.now()
                // Remember which tack is running so it can be switched OFF once
                // the action completes (see the release timer above).
                activeTackMappingRef.current = mapping ?? null
                holdMomentaryLock()
            }
            return true
        },
        [
            showNotice,
            holdMomentaryLock,
            releaseMomentaryLock,
            webSerial?.isConnected,
        ]
    )

    // Listen for the device's "Rdone" response (\r\nRdone\r\n) — it means the
    // last tack action finished, so release the momentary-button lock. The
    // worker forwards every incoming serial line as a 'read' / 'actionRead'
    // message whose stringData contains the decoded text. A "busy" reply means
    // the action is still running and takes precedence over "Rdone".
    useEffect(() => {
        if (!worker) return
        const handleWorkerMessage = (e) => {
            const msg = e.data || {}
            let str = ''
            if (msg.type === 'read') str = msg.value
            else if (msg.type === 'actionRead') str = msg.value?.stringData
            else return
            if (typeof str !== 'string') return

            const hasRdone = str.includes('Rdone')
            const hasBusy = str.includes('busy')
            if (!hasRdone && !hasBusy) return

            console.log(
                `[TACK-GATE] serial=${JSON.stringify(str)} ` +
                `Rdone=${hasRdone} busy=${hasBusy} locked=${awaitingResponseRef.current}`
            )

            if (awaitingResponseRef.current) {
                if (hasBusy) {
                    holdMomentaryLock()
                    showNotice('Please wait for the action to complete')
                } else {
                    releaseMomentaryLock('Rdone')
                }
            } else if (
                hasBusy &&
                Date.now() - lastPressAtRef.current < PRESS_RELEVANCE_MS
            ) {
                // The hold expired but the board is still busy with a recent
                // action — a slipped press revealed it. Re-lock and warn.
                console.log('[TACK-GATE] busy after release → re-locking')
                holdMomentaryLock()
                showNotice('Please wait for the action to complete')
            }
        }
        worker.addEventListener('message', handleWorkerMessage)
        return () => worker.removeEventListener('message', handleWorkerMessage)
    }, [worker, holdMomentaryLock, releaseMomentaryLock, showNotice])

    // Clear pending timers on unmount.
    useEffect(() => {
        return () => {
            if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
            if (releaseTimerRef.current)
                clearTimeout(releaseTimerRef.current)
        }
    }, [])

    const handleControlChange = (mapping, val) => {
        setUserInteractedThisStep(true) // Unlock auto-advance

        // Compute the effective numeric value (handles 'clicked' toggle)
        const currentRaw = outputStates[mapping] || 0
        const effectiveVal =
            val === 'clicked' ? (currentRaw > 0 ? 0 : 100) : Number(val)

        // Keep outputStates in sync; the global eval useEffect rebuilds outputImages
        setOutputStates((prev) => ({ ...prev, [mapping]: effectiveVal }))
    }

    const generateSliderProps = (mapping, compName) => {
        const val = outputStates[mapping] || 0
        const setVal = (v) => handleControlChange(mapping, v)

        const baseProps = {
            setL_Green: setVal,
            isL_Green: val,
            setL_Blue: setVal,
            isL_Blue: val,
            setL_Red: setVal,
            isL_Red: val,
            setR_Green: setVal,
            isR_Green: val,
            setR_Blue: setVal,
            isR_Blue: val,
            setR_Red: setVal,
            isR_Red: val,
            setFreq: setVal,
            isFreq: val,
            value: val,
            val: val,
        }

        if (compName) {
            baseProps[`is${compName}`] = val
            baseProps[`set${compName}`] = setVal
        }

        return baseProps
    }

    const isHighlighted = (compId) => highlightedComponentIds.has(compId)
    const highlightClass = (compId) =>
        isHighlighted(compId) ? ' play-highlight' : ''

    // Helper: find the best matching state image by checking most-specific states first.
    // States with more conditions are more specific and should be evaluated before broad ones.
    const resolveStateImage = (states, defaultImg) => {
        if (!states || !Array.isArray(states)) return defaultImg
        // Sort by specificity: more condition blocks (or more conditions in first block) = higher priority
        const sorted = [...states].sort((a, b) => {
            const aLen = a.conditionBlocks
                ? a.conditionBlocks.reduce((s, blk) => s + blk.length, 0)
                : a.conditions?.length || 0
            const bLen = b.conditionBlocks
                ? b.conditionBlocks.reduce((s, blk) => s + blk.length, 0)
                : b.conditions?.length || 0
            return bLen - aLen
        })
        for (const state of sorted) {
            let conditionsMet
            if (state.conditionBlocks) {
                // New schema: OR across blocks, AND within each block
                conditionsMet = state.conditionBlocks.some((block) =>
                    block.every((cond) => evaluateCondition(cond))
                )
            } else {
                // Legacy schema: flat AND conditions
                conditionsMet = (state.conditions || []).every((cond) =>
                    evaluateCondition(cond)
                )
            }
            if (conditionsMet) return state.image
        }
        return defaultImg
    }

    // --- Shell: stable onStepClick for header timeline nav ---
    const onStepClick = useCallback(
        (stepNum) => {
            setUserInteractedThisStep(false)
            setOutputStates(buildStateUpToStep(stepNum - 1, data?.steps || []))
            ContentEngine.breakSequentialChain()
            setCurrentStep(stepNum)
            if (aiVoiceover) playAudioForStep(stepNum)
            setShowSubtitleDisplay(true)
        },
        [aiVoiceover, playAudioForStep, data]
    )

    // --- Shell: register header props ---
    useEffect(() => {
        if (!setHeaderProps) return
        setHeaderProps({
            title: data?.title || 'Play Mode',
            titlePosition: 'left',
            help_btnPosition: 'right',
            isGoBack: true,
            goBack,
            showHelp: true,
            showBluetoothBtn: true,
            showUsbBtn: true,
            useUsbIcon: true,
            currentStep: hasSteps
                ? Math.min(currentStep, totalSteps)
                : undefined,
            totalSteps: hasSteps ? totalSteps : undefined,
            stepTitles: hasSteps
                ? data?.steps?.map((s) => s?.title || '') || []
                : [],
            onStepClick,
        })
    }, [
        setHeaderProps,
        data,
        currentStep,
        totalSteps,
        hasSteps,
        goBack,
        onStepClick,
    ])

    // --- Shell: register footer props ---
    useEffect(() => {
        if (!setFooterProps) return
        const isFinalStep = hasSteps && currentStep > totalSteps
        const isFirstStep = !hasSteps || currentStep <= 1
        const stepCounter = hasSteps
            ? isFinalStep
                ? 'Complete'
                : `Step ${currentStep} of ${totalSteps}`
            : ''
        const stepTitle = hasSteps
            ? currentStepData?.title || ''
            : data?.title || 'Play Mode'
        const nextDisabled = (isFinalStep && !dataProp) || !isStepValid
        const nextClassName = currentStepData?.isFreeStep
            ? 'free-step-highlight'
            : ''
        setFooterProps({
            stepCounter,
            stepTitle,
            prevDisabled: isFirstStep && !dataProp,
            nextDisabled,
            nextLabel: isFinalStep ? 'Continue' : 'Next',
            nextClassName,
            showNextTooltip: !isStepValid,
            nextTooltipText: 'Complete the action to proceed',
            onPrev: handleStepPrev,
            onNext: handleStepNext,
        })
    }, [
        setFooterProps,
        hasSteps,
        currentStep,
        totalSteps,
        currentStepData,
        dataProp,
        isStepValid,
        handleStepPrev,
        handleStepNext,
        data,
    ])

    // --- Shell: register toolbar props ---
    useEffect(() => {
        if (!setToolbarProps) return
        setToolbarProps({
            aiVoiceover,
            onAiToggle: () => {
                handleAiVoiceoverToggle()
                setSubtitleVisible(true)
                setShowSubtitleDisplay(true)
            },
            onFullscreen: handleFullscreen,
            onReset: resetTutorial,
        })
    }, [
        setToolbarProps,
        aiVoiceover,
        handleAiVoiceoverToggle,
        handleFullscreen,
        resetTutorial,
    ])

    const renderComponent = (comp) => {
        const isVisible =
            !visibleComponentIds || visibleComponentIds.has(comp.id)

        // 1. Interactive UI disabling logic
        // Only components capable of interaction (sliders, buttons) will be blocked.
        const isInteractiveType = [
            'button_control',
            'tack_button_control',
            'hold_button_control',
            'trigger_button_control',
            'ack_button_control',
            'spam_button_control',
            'press_release_button_control',
            'slider_control',
            'custom_slider_control',
            'rgb_control',
            'rgb_button_control',
        ].includes(comp.type)

        // Backwards compatibility fallback: if the 'enableComponents' array doesn't exist on an old saved step, fall back to the old highlight logic.
        const hasEnableArray =
            currentStepData && currentStepData.enableComponents !== undefined

        const isInteractable = hasEnableArray
            ? enabledComponentIds.has(comp.id)
            : !hasSteps ||
            highlightedComponentIds.size === 0 ||
            isHighlighted(comp.id)

        // Apply pointerEvents: 'none' and visual dimming to ALL disabled components consistently.
        const interactiveStyle =
            hasEnableArray && !isInteractable
                ? {
                    pointerEvents: 'none',
                    opacity: 0.4,
                    filter: 'grayscale(60%)',
                }
                : !isInteractable
                    ? { pointerEvents: 'none' }
                    : {}

        const hiddenStyle = !isVisible
            ? { visibility: 'hidden', pointerEvents: 'none' }
            : {}

        // Admin-configurable size for interactive inputs. scale=1 → current size.
        // Applied as a CSS transform so the aspect ratio is always preserved.
        const scaleVal = Number(comp.properties?.scale)
        const inputScale =
            isInteractiveType && Number.isFinite(scaleVal) && scaleVal > 0
                ? scaleVal
                : 1
        const baseTransform =
            comp.position.anchor === 'center' ? 'translate(-50%, -50%)' : 'none'
        const scaledTransform =
            inputScale !== 1
                ? `${baseTransform === 'none' ? '' : baseTransform} scale(${inputScale})`.trim()
                : baseTransform

        const style = {
            left: `${comp.position.x * 100}%`,
            top: `${comp.position.y * 100}%`,
            transform: scaledTransform,
            // Exposed so the text label can counter-scale itself and stay the
            // same size while the control image scales.
            '--input-scale': inputScale,
            ...hiddenStyle,
            ...interactiveStyle,
        }

        const device = sessionStorage.getItem('allowedDevice') || 'Toki'

        switch (comp.type) {
            case 'board': // canonical type (new saves)
            case 'toki_board': // legacy backward-compat
                // Determine the specific height rule for the board type
                const boardSpecificStyle =
                    device === 'Poki'
                        ? { height: '40%', width: 'auto' } // Scales down the Poki board relative to the viewport
                        : { height: '83%', width: 'auto' } // Preserves default Toki height

                // The Toki board artwork is NOT horizontally centered inside its
                // image file: the physical board's centre sits at 2180px in a
                // 3840px-wide image, i.e. (2180 - 1920) / 3840 ≈ 6.77% to the
                // right of the image centre. Nudge the image left by that
                // fraction of its own width so the actual board (not the image)
                // lands on comp.position.x. translate() percentages are relative
                // to the element's own width, so this holds at any rendered scale.
                const boardArtOffsetX =
                    device === 'Toki' ? -((2180 - 3840 / 2) / 3840) * 100 : 0 // % of image width
                const boardTranslateX =
                    (comp.position.anchor === 'center' ? -50 : 0) +
                    boardArtOffsetX
                const boardTranslateY =
                    comp.position.anchor === 'center' ? -50 : 0

                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component board-img${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            ...boardSpecificStyle,
                            transform: `translate(${boardTranslateX}%, ${boardTranslateY}%)`,
                        }}
                        src={resolveImage(comp.properties.image)}
                        alt={comp.name}
                        draggable={false}
                        onLoad={calculateWires}
                    />
                )
            case 'model': {
                // 1. Check if there's an active interaction override for this model
                const override = modelOverrides[comp.id] || {}
                const activeModelUrl = override.modelUrl || comp.modelUrl
                const activeAnimation =
                    override.animationName ||
                    currentStepData?.models?.animationName
                // 1. Check for camera orbit (theta, phi, radius) instead of x,y,z orientation
                const activeOrbit = override.cameraOrbit || comp.cameraOrbit

                // 2. Format: "theta(deg) phi(deg) radius(%)"
                const cameraOrbitAttr = activeOrbit
                    ? `${activeOrbit.theta || 0}deg ${activeOrbit.phi || 75}deg ${activeOrbit.radius || 100}%`
                    : undefined

                // 2. Resolve the final URL
                const resolvedUrl = resolveModelUrl(activeModelUrl)

                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component playscreen-model-viewer${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            width: comp.width || '350px',
                            height: comp.height || '450px',
                            zIndex: 5,
                        }}
                    >
                        <model-viewer
                            // Using resolvedUrl as the key forces React to completely remount the viewer
                            // if a completely different .glb file is loaded, avoiding crash errors.
                            key={resolvedUrl}
                            ref={(el) => (modelRefs.current[comp.id] = el)}
                            src={resolvedUrl}
                            alt={comp.name}
                            camera-controls="true"
                            shadow-intensity="1"
                            loading="eager"
                            // Pass the animation name dynamically
                            animation-name={activeAnimation || undefined}
                            // Force autoplay if there's an animation active
                            autoplay={
                                comp.autoplay || activeAnimation
                                    ? 'true'
                                    : undefined
                            }
                            camera-orbit={cameraOrbitAttr}
                            style={{
                                width: '100%',
                                height: '100%',
                                outline: 'none',
                                backgroundColor: 'transparent',
                            }}
                        />
                    </div>
                )
            }
            case 'image': {
                // Simple display-only image. scale=1 → 20vw base width; the image
                // keeps its natural aspect ratio (height auto).
                const scale = Number(comp.properties.scale)
                const safeScale = Number.isFinite(scale) ? scale : 1
                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component playscreen-image${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            width: `${safeScale * 20}vw`,
                            height: 'auto',
                            zIndex: 8,
                        }}
                        src={resolveImage(comp.properties.imageUrl)}
                        alt={comp.name || 'image'}
                        draggable={false}
                    />
                )
            }
            case 'output': {
                // Prefer input-driven outputImages; fall back to legacy resolveStateImage
                const imgSrcToRender =
                    outputImages[comp.id] ||
                    resolveStateImage(
                        comp.properties.states,
                        comp.properties.defaultImage
                    ) ||
                    comp.properties.offImage

                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component output-img ${imgSrcToRender === 'dynamexMotor@2x.png'
                            ? 'output-dynamex'
                            : ''
                            }${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            zIndex: 10,
                            width: '800px !important',
                            height: 'auto !important',
                        }}
                        src={resolveImage(imgSrcToRender)}
                        alt={comp.name}
                        draggable={false}
                        onLoad={calculateWires}
                    />
                )
            }
            case 'custom_output': {
                // Prefer input-driven outputImages; fall back to legacy resolveStateImage
                const imgSrcToRender =
                    outputImages[comp.id] ||
                    resolveStateImage(
                        comp.properties.states,
                        comp.properties.defaultImage
                    ) ||
                    comp.properties.offImage

                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component output-img${highlightClass(comp.id)}`}
                        style={{ ...style, zIndex: 10 }}
                        src={resolveImage(imgSrcToRender)}
                        alt={comp.name}
                        draggable={false}
                        onLoad={calculateWires}
                    />
                )
            }
            case 'button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                        onRequestActivate={() =>
                            requestMomentaryPress(
                                'hold',
                                comp.id,
                                false,
                                !!comp.properties.bypassLock
                            )
                        }
                    />
                )
            }
            case 'tack_button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveTackButtonControl
                        key={`${comp.id}-${currentStep}`} // <-- Force sync on step jump
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                        onRequestActivate={() =>
                            requestMomentaryPress(
                                'tack',
                                comp.id,
                                !!comp.properties.stopAction,
                                !!comp.properties.bypassLock
                            )
                        }
                    />
                )
            }
            case 'hold_button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveHoldButtonControl
                        key={`${comp.id}-${currentStep}`} // <-- Force sync on step jump
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                        onRequestActivate={() =>
                            requestMomentaryPress(
                                'hold',
                                comp.id,
                                !!comp.properties.stopAction,
                                !!comp.properties.bypassLock
                            )
                        }
                    />
                )
            }
            case 'trigger_button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveTriggerButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                        onRequestActivate={() =>
                            requestMomentaryPress(
                                'hold',
                                comp.id,
                                false,
                                !!comp.properties.bypassLock
                            )
                        }
                    />
                )
            }
            case 'ack_button_control': {
                // PM-1: single byte on tap, self-locks until custom ack received.
                // PM-4: actionLock=true also arms the global gate (blocks all other buttons).
                // onActionComplete: releases the global gate when the custom ack arrives,
                //   needed when ackPattern != 'Rdone' (PlayScreen's own listener won't fire).
                const bridgeComp = { ...comp, mapping: comp.id }
                const actionLock = comp.properties.actionLock ?? false
                const bypassLock = !!comp.properties.bypassLock
                return (
                    <LiveAckButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        bypassLock={bypassLock}
                        onRequestActivate={() =>
                            requestMomentaryPress(
                                actionLock ? 'tack' : 'hold',
                                comp.id,
                                false,
                                bypassLock
                            )
                        }
                        onActionComplete={
                            actionLock
                                ? () => releaseMomentaryLock('custom-ack')
                                : undefined
                        }
                    />
                )
            }
            case 'spam_button_control': {
                // PM-2: hold-to-spam. Blocked by PM-4 lock unless bypassLock=true.
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveSpamButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        onRequestActivate={() =>
                            requestMomentaryPress(
                                'hold',
                                comp.id,
                                false,
                                !!comp.properties.bypassLock
                            )
                        }
                    />
                )
            }
            case 'press_release_button_control': {
                // PM-3: press/release. Blocked by PM-4 lock unless bypassLock=true.
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LivePressReleaseButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        onRequestActivate={() =>
                            requestMomentaryPress(
                                'hold',
                                comp.id,
                                false,
                                !!comp.properties.bypassLock
                            )
                        }
                    />
                )
            }
            case 'slider_control': {
                // PM-6/7/8: use LiveRangeSliderControl if sliderType is explicitly set,
                // OR if startValue/endValue are configured (component created before sliderType was added).
                const hasPmConfig =
                    comp.properties.sliderType !== undefined ||
                    comp.properties.startValue !== undefined ||
                    comp.properties.endValue !== undefined
                if (hasPmConfig) {
                    return (
                        <LiveRangeSliderControl
                            key={comp.id}
                            comp={comp}
                            outputStates={outputStates}
                            onChange={handleControlChange}
                            highlightClass={highlightClass(comp.id)}
                            style={style}
                            isLiveStep={currentStepData?.isLiveStep}
                        />
                    )
                }

                // Legacy: Component type ranges from PortValuesRangeMapping / SliderRow
                const COMPONENT_RANGES = {
                    mini_geared_motor: { min: -100, max: 100, showLive: true },
                    geared_motor: { min: -100, max: 100, showLive: true },
                    dc_motor: { min: -100, max: 100, showLive: true },
                    dynamex_motor: { min: -100, max: 100, showLive: true },
                    servo_motor: { min: 0, max: 180, showLive: true },
                    servo_motor_270: { min: 0, max: 270, showLive: true },
                    servo_motor_sg90: { min: 0, max: 180, showLive: true },
                    servo_motor_360: { min: -100, max: 100, showLive: true },
                    pwm_3_pin_output: { min: 0, max: 100, showLive: true },
                    led: { min: 0, max: 100, showLive: true },
                    led_1c: { min: 0, max: 100, showLive: true },
                    digital_3_pin_output: { min: 0, max: 255, showLive: true },
                    analog_3_pin_input: { min: 0, max: 4095, showLive: false },
                    digital_3_pin_input: { min: 0, max: 1, showLive: false },
                    ultrasonic_sensor: { min: 0, max: 400, showLive: false },
                }

                const compType = comp.name.toLowerCase()
                const range = COMPONENT_RANGES[compType] || {
                    min: 0,
                    max: 100,
                    showLive: false,
                }

                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component slider-wrapper${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            zIndex: 20,
                        }}
                    >
                        <MotorSlider
                            mapping={comp.id}
                            outputStates={outputStates}
                            onChange={handleControlChange}
                            rangImgName={comp.properties.rangImgName}
                            title={comp.properties.title || 'Intensity'}
                            label={comp.properties.label || comp.name}
                            min={range.min}
                            max={range.max}
                            showLiveBtn={range.showLive}
                            port={comp.port}
                            componentType={compType}
                            isLiveStep={currentStepData?.isLiveStep}
                        />
                    </div>
                )
            }
            case 'custom_slider_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                const dynamicProps = generateSliderProps(
                    bridgeComp.mapping,
                    comp.properties.legacyName || bridgeComp.mapping
                )
                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component slider-wrapper${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            zIndex: 20,
                            width: '250px',
                            height: '60px',
                        }}
                    >
                        <SliderRange
                            rangImgName={comp.properties.rangImgName}
                            title={comp.properties.title}
                            componentName={
                                comp.properties.legacyName || bridgeComp.mapping
                            }
                            onChangehandler={(val) =>
                                handleControlChange(bridgeComp.mapping, val)
                            }
                            leftEyeData={() => { }}
                            disable={false}
                            webSerial={{ isConnected: true }}
                            {...dynamicProps}
                        />
                        <span className="control-label">{comp.name}</span>
                    </div>
                )
            }
            case 'rgb_control': {
                const rgbSliders = [
                    {
                        name: 'Red',
                        mapping: `${comp.id}_red`,
                        legacyName: 'L_red',
                        activeImg: 'red_slider',
                        inactiveImg: 'inactiveslider',
                    },
                    {
                        name: 'Green',
                        mapping: `${comp.id}_green`,
                        legacyName: 'L_green',
                        activeImg: 'green_slider',
                        inactiveImg: 'inactiveslider',
                    },
                    {
                        name: 'Blue',
                        mapping: `${comp.id}_blue`,
                        legacyName: 'L_blue',
                        activeImg: 'blue_slider',
                        inactiveImg: 'inactiveslider',
                    },
                ]

                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component rgb-control-wrapper${highlightClass(comp.id)}`}
                        style={{ ...style, zIndex: 20 }}
                    >
                        <RGBSlider
                            sliders={rgbSliders}
                            outputStates={outputStates}
                            onChange={handleControlChange}
                            generateSliderProps={generateSliderProps}
                            bgImage={resolveImage("renderImage('Eye_bg_Svg')")}
                            label={comp.properties.label}
                            port={comp.port}
                            isLiveStep={currentStepData?.isLiveStep}
                        />
                    </div>
                )
            }
            case 'rgb_button_control': {
                // Merged into LiveButtonControl – RGB mode is detected via comp.name === 'RGB'
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                    />
                )
            }
            default:
                return null
        }
    }

    if (loading)
        return (
            <div className="play-app-container centered-msg">
                Loading Play Screen...
            </div>
        )
    if (!data || !data.components)
        return (
            <div className="play-app-container centered-msg">
                No Play Screen Data
            </div>
        )

    const isFinalStep =
        hasSteps && currentStep > totalSteps && !hideFinalStep
    const isFirstStep = !hasSteps || currentStep <= 1
    // In Kit Menu play the last real step is terminal (no Congratulations step),
    // so the Next button is disabled once the user reaches it.
    const isTerminalStep = hideFinalStep && currentStep >= totalSteps
    // Kit Menu play with a single step has nothing to navigate, so hide the
    // footer entirely.

    const hideFooter = hideFinalStep && totalSteps <= 1

    function ActionIcon() {
        return (
            <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.2741 0.100098C10.6054 0.100098 9.95172 0.29839 9.39571 0.669899C8.83971 1.04141 8.40636 1.56945 8.15046 2.18724C7.89456 2.80504 7.82761 3.48485 7.95806 4.1407C8.08852 4.79655 8.41053 5.39898 8.88337 5.87183C9.35621 6.34467 9.95865 6.66668 10.6145 6.79713C11.2703 6.92759 11.9502 6.86063 12.568 6.60473C13.1857 6.34883 13.7138 5.91548 14.0853 5.35948C14.4568 4.80348 14.6551 4.1498 14.6551 3.4811C14.6551 3.0371 14.5676 2.59745 14.3977 2.18724C14.2278 1.77704 13.9788 1.40432 13.6648 1.09037C13.0308 0.456309 12.1708 0.100098 11.2741 0.100098ZM11.2741 5.9531C10.7852 5.9531 10.3072 5.80812 9.90073 5.53649C9.49421 5.26486 9.17737 4.87879 8.99027 4.42709C8.80317 3.97539 8.75421 3.47836 8.8496 2.99883C8.94498 2.51931 9.18041 2.07885 9.52613 1.73313C9.87185 1.38741 10.3123 1.15198 10.7918 1.0566C11.2714 0.961214 11.7684 1.01017 12.2201 1.19727C12.6718 1.38437 13.0579 1.70121 13.3295 2.10773C13.6011 2.51425 13.7461 2.99218 13.7461 3.4811C13.7461 3.80573 13.6822 4.12717 13.5579 4.42709C13.4337 4.72701 13.2516 4.99952 13.0221 5.22907C12.7925 5.45861 12.52 5.6407 12.2201 5.76493C11.9202 5.88916 11.5987 5.9531 11.2741 5.9531ZM13.8271 7.8961H8.7201C8.50164 7.89636 8.29221 7.98326 8.13773 8.13773C7.98326 8.29221 7.89636 8.50164 7.8961 8.7201V13.8271C7.89636 14.0456 7.98326 14.255 8.13773 14.4095C8.29221 14.5639 8.50164 14.6508 8.7201 14.6511H13.8271C14.0456 14.6508 14.255 14.5639 14.4095 14.4095C14.5639 14.255 14.6508 14.0456 14.6511 13.8271V8.7201C14.6508 8.50164 14.5639 8.29221 14.4095 8.13773C14.255 7.98326 14.0456 7.89636 13.8271 7.8961ZM13.7421 13.7411H8.8061V8.8061H13.7421V13.7411ZM0.928098 6.8581H6.0341C6.25255 6.85783 6.46199 6.77093 6.61646 6.61646C6.77093 6.46199 6.85783 6.25255 6.8581 6.0341V0.928098C6.85783 0.709641 6.77093 0.500207 6.61646 0.345734C6.46199 0.191261 6.25255 0.104362 6.0341 0.104098H0.928098C0.709641 0.104362 0.500207 0.191261 0.345734 0.345734C0.191261 0.500207 0.104362 0.709641 0.104098 0.928098V6.0341C0.104362 6.25255 0.191261 6.46199 0.345734 6.61646C0.500207 6.77093 0.709641 6.85783 0.928098 6.8581ZM1.0131 1.0131H5.9491V5.9491H1.0131V1.0131ZM3.4811 14.6551C4.1498 14.6551 4.80348 14.4568 5.35948 14.0853C5.91548 13.7138 6.34883 13.1857 6.60473 12.568C6.86063 11.9502 6.92759 11.2703 6.79713 10.6145C6.66668 9.95865 6.34467 9.35621 5.87183 8.88337C5.39898 8.41053 4.79655 8.08852 4.1407 7.95806C3.48485 7.82761 2.80504 7.89456 2.18724 8.15046C1.56945 8.40636 1.04141 8.83971 0.669899 9.39571C0.29839 9.95172 0.100098 10.6054 0.100098 11.2741C0.100098 12.1708 0.456309 13.0308 1.09037 13.6648C1.72443 14.2989 2.5844 14.6551 3.4811 14.6551ZM3.4811 8.8021C3.97001 8.8021 4.44795 8.94708 4.85447 9.2187C5.26099 9.49033 5.57783 9.87641 5.76493 10.3281C5.95203 10.7798 6.00098 11.2768 5.9056 11.7564C5.81022 12.2359 5.57478 12.6763 5.22907 13.0221C4.88335 13.3678 4.44288 13.6032 3.96336 13.6986C3.48384 13.794 2.9868 13.745 2.5351 13.5579C2.08341 13.3708 1.69733 13.054 1.4257 12.6475C1.15408 12.2409 1.0091 11.763 1.0091 11.2741C1.0091 10.6185 1.26954 9.98972 1.73313 9.52613C2.19672 9.06254 2.82548 8.8021 3.4811 8.8021Z" fill="currentColor" stroke="white" stroke-width="0.2" />
            </svg>

        )
    }
    function DanceIcon() {
        return (
            <svg width="21" height="20" viewBox="0 0 21 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.9323 4.52688C9.88216 4.5267 9.83203 4.52974 9.78227 4.53591V2.04495C9.7815 1.99872 9.79001 1.95262 9.80718 1.90969C9.82435 1.86677 9.84993 1.82789 9.88237 1.79495C9.94916 1.7298 10.0391 1.69373 10.1324 1.69485C10.2263 1.69485 10.3162 1.73222 10.3826 1.79861C10.449 1.865 10.4863 1.95497 10.4863 2.04885V4.53982C10.4362 4.53361 10.3857 4.53057 10.3353 4.53079L9.9323 4.52688Z" fill="currentColor" />
                <path d="M10.5364 4.6326V4.96658C10.5362 5.0397 10.5159 5.11145 10.4778 5.17386C10.4397 5.23627 10.3852 5.28696 10.3203 5.32059H9.95239C9.88748 5.28696 9.833 5.23627 9.79492 5.17386C9.75684 5.11145 9.73654 5.0397 9.73633 4.96658V4.6326L9.78235 4.62674C9.83237 4.61968 9.88284 4.61633 9.93335 4.61673H10.3394C10.3899 4.61662 10.4403 4.61997 10.4904 4.62674L10.5364 4.6326Z" fill="currentColor" />
                <path d="M10.5354 4.63477C10.5206 4.63121 10.5056 4.62903 10.4905 4.62793V4.96875C10.4905 5.06264 10.4531 5.15285 10.3867 5.21924C10.3203 5.28563 10.2304 5.32275 10.1365 5.32275C10.0426 5.32275 9.9525 5.28563 9.88611 5.21924C9.81972 5.15285 9.78247 5.06264 9.78247 4.96875V4.62793C9.76736 4.62894 9.75228 4.63081 9.73743 4.63379C9.72227 4.63593 9.7072 4.63896 9.69238 4.64282V4.96875C9.69242 5.03843 9.70892 5.10725 9.74036 5.16943C9.77179 5.23161 9.81732 5.28557 9.87341 5.3269C9.89239 5.32572 9.91144 5.32572 9.93042 5.3269H10.3374C10.3584 5.32553 10.3794 5.32553 10.4004 5.3269C10.4568 5.28579 10.5027 5.23189 10.5343 5.16968C10.5659 5.10746 10.5824 5.03854 10.5824 4.96875V4.64282C10.5669 4.63933 10.5512 4.6366 10.5354 4.63477Z" fill="#25245E" />
                <path d="M10.4926 0.371826C10.4928 0.418304 10.484 0.464321 10.4663 0.507324C10.4487 0.550327 10.4227 0.589403 10.3899 0.622314C10.3571 0.655226 10.318 0.681463 10.275 0.699219C10.2321 0.716974 10.1861 0.725963 10.1397 0.72583C10.0932 0.725962 10.0471 0.7169 10.0042 0.699219C9.96122 0.681538 9.9223 0.655595 9.88942 0.622803C9.85653 0.590011 9.83044 0.55095 9.81264 0.508057C9.79483 0.465163 9.78566 0.419244 9.78566 0.372803C9.78311 0.324799 9.79029 0.2768 9.8069 0.231689C9.82351 0.186579 9.84914 0.145235 9.88222 0.110352C9.91529 0.0754684 9.95512 0.0477934 9.99928 0.0288086C10.0434 0.00982377 10.0911 0 10.1392 0C10.1872 0 10.2348 0.00982377 10.2789 0.0288086C10.3231 0.0477934 10.3629 0.0754684 10.396 0.110352C10.4291 0.145235 10.4547 0.186579 10.4713 0.231689C10.4879 0.2768 10.4951 0.323822 10.4926 0.371826Z" fill="#25245E" />
                <path d="M16.5293 11.9407C16.3243 10.674 15.7476 9.49667 14.8723 8.55838C13.997 7.62008 12.8627 6.96315 11.6133 6.67068L11.5214 6.63065V5.80057C11.5206 5.52903 11.4267 5.26594 11.2555 5.05521C11.0843 4.84447 10.846 4.69888 10.5803 4.64261L10.5343 4.63455L10.4894 4.62772C10.4393 4.62094 10.3889 4.61759 10.3384 4.61771H9.9314C9.88089 4.61732 9.83041 4.62067 9.7804 4.62772L9.73535 4.63357L9.69031 4.64261C9.42448 4.69868 9.18597 4.84421 9.01453 5.05496C8.84309 5.26571 8.74908 5.52889 8.74829 5.80057V6.64969L8.71228 6.65774C7.86024 6.84898 7.05625 7.21194 6.34937 7.72464C6.32521 7.73833 6.30271 7.75484 6.28235 7.77371C5.47059 8.37541 4.81112 9.15882 4.35669 10.0613C3.90226 10.9638 3.66553 11.9603 3.66553 12.9707C3.66553 13.9812 3.90226 14.9774 4.35669 15.8799C4.81112 16.7824 5.47059 17.5658 6.28235 18.1675C6.30198 18.1851 6.3234 18.2006 6.34631 18.2137C7.38485 18.9631 8.62232 19.3877 9.90222 19.4339C11.1821 19.48 12.4469 19.1457 13.5367 18.4729C14.6265 17.8002 15.4925 16.8192 16.0248 15.6543C16.5571 14.4895 16.732 13.193 16.5273 11.9287L16.5293 11.9407ZM11.7163 7.43265C12.2924 7.59523 12.8394 7.84737 13.3373 8.17972L13.3513 8.21268L13.3654 8.25369C13.5652 9.00594 13.7168 9.76999 13.8193 10.5415L13.8274 10.6026L13.7664 10.5926C13.2244 10.5026 12.6744 10.4346 12.1304 10.3926H12.0923V10.3557C11.9998 9.39446 11.8563 8.43875 11.6624 7.49271L11.6473 7.41971L11.7163 7.43265ZM9.4563 5.79764C9.45662 5.68184 9.49917 5.57013 9.57593 5.48343C9.65269 5.39673 9.75839 5.34104 9.87329 5.32669C9.8926 5.32546 9.91208 5.32546 9.9314 5.32669H10.3384C10.3594 5.32518 10.3804 5.32518 10.4014 5.32669C10.5151 5.34233 10.6194 5.39831 10.6951 5.48465C10.7707 5.57098 10.8127 5.68185 10.8134 5.79666V6.5347L10.7633 6.52957C10.3456 6.48939 9.92503 6.48939 9.50732 6.52957H9.4574L9.4563 5.79764ZM8.82239 10.2796C8.92386 9.27794 9.08274 8.28299 9.29834 7.29959L9.3324 7.26468C9.8377 7.19399 10.3501 7.19031 10.8563 7.25369H10.8883L10.8954 7.2847C11.1105 8.27051 11.269 9.26761 11.3704 10.2715L11.3763 10.3235H11.3243C10.5069 10.2791 9.68776 10.2791 8.87036 10.3235H8.81738L8.82239 10.2796ZM6.37134 10.5547C6.47777 9.77756 6.62934 9.00713 6.82532 8.24759L6.64233 8.38455L6.84229 8.24466C7.3397 7.89679 7.88926 7.63009 8.47034 7.45462L8.54333 7.43265L8.52832 7.5076C8.33641 8.44808 8.19461 9.39803 8.10339 10.3535V10.3907H8.06628C7.52228 10.4417 6.97132 10.5126 6.42932 10.6016L6.36829 10.6116L6.37134 10.5547ZM4.77136 10.8545C5.01505 10.2408 5.36243 9.67353 5.79834 9.17752L5.8783 9.0867V9.21658C5.7783 9.70958 5.69828 10.2166 5.63928 10.7166V10.7495L5.56128 10.7647L4.74536 10.9317L4.77136 10.8545ZM4.51538 14.2805V14.2627C4.32678 13.4299 4.32367 12.5658 4.50635 11.7317L4.51428 11.6966H4.56738L5.56738 11.4917V11.5515C5.49465 12.5135 5.49465 13.4797 5.56738 14.4417V14.5005L4.55139 14.3006L4.51538 14.2805ZM5.79529 16.7805C5.37517 16.301 5.03728 15.7554 4.79529 15.1656L4.6853 15.1436L4.85132 15.0616L4.86133 15.0867L5.6333 15.2376V15.2705C5.6933 15.7605 5.77138 16.2557 5.86938 16.7417L5.90234 16.9087L5.79529 16.7805ZM6.31934 14.6416L6.2843 14.6355V14.6006C6.19105 13.531 6.19105 12.4552 6.2843 11.3855H6.32532L6.31836 11.3416C6.87136 11.2416 7.43328 11.1676 7.98828 11.1126L8.04138 11.1077V11.1607C8.00538 11.7197 7.9854 12.2877 7.9834 12.8487C7.9834 13.4997 8.00639 14.1596 8.05139 14.8106V14.8636L7.99829 14.8587C7.43929 14.8057 6.87531 14.7336 6.32031 14.6336L6.31934 14.6416ZM8.49329 18.5096C7.91041 18.3363 7.35893 18.0714 6.85938 17.7246L6.83533 17.7307L6.82129 17.6885C6.62469 16.9453 6.47475 16.1907 6.37231 15.4287L6.36438 15.3677L6.42432 15.3777C6.97032 15.4657 7.52439 15.5337 8.07239 15.5777H8.11035V15.6145C8.2064 16.5672 8.35259 17.5144 8.54834 18.4517L8.56335 18.5266L8.49329 18.5096ZM11.3573 15.6907C11.2523 16.6904 11.0904 17.6833 10.8723 18.6646V18.6746V18.6997H10.8324C10.6002 18.7285 10.3663 18.7428 10.1323 18.7427C9.87277 18.7423 9.61353 18.7246 9.35632 18.6897H9.32532L9.31836 18.6587C9.10003 17.6775 8.93842 16.6844 8.83435 15.6846L8.82935 15.6316H8.88232C9.29632 15.6536 9.71732 15.6646 10.1323 15.6656C10.5223 15.6656 10.9183 15.6555 11.3093 15.6355H11.3623L11.3573 15.6907ZM13.8163 15.4397C13.7117 16.2191 13.5575 16.9908 13.3544 17.7505L13.3453 17.7847H13.3203C12.8203 18.1184 12.271 18.3715 11.6924 18.5347L11.6204 18.5547L11.6354 18.4817C11.833 17.5389 11.9806 16.5862 12.0774 15.6277V15.5906H12.1144C12.6604 15.5456 13.2144 15.4787 13.7604 15.3907L13.8203 15.3816L13.8163 15.4397ZM13.9093 14.6136V14.6546H13.8673C13.3143 14.7446 12.7524 14.8167 12.1974 14.8697H12.1443V14.8167C12.1513 14.7167 12.1783 13.1296 12.1853 13.0276C12.188 12.9812 12.1815 12.9348 12.1663 12.8909C12.151 12.847 12.1272 12.8065 12.0963 12.7718C12.0654 12.737 12.0279 12.7085 11.9861 12.6883C11.9442 12.668 11.8987 12.6563 11.8523 12.6536C11.8059 12.6509 11.7594 12.6574 11.7155 12.6726C11.6715 12.6879 11.631 12.7118 11.5963 12.7427C11.5616 12.7736 11.5333 12.811 11.5131 12.8528C11.4928 12.8947 11.4811 12.9402 11.4784 12.9866C11.4704 13.1236 11.4403 14.7445 11.4293 14.8865V14.9265H11.3893C10.9733 14.9495 10.5544 14.9617 10.1354 14.9617C9.69238 14.9617 9.24933 14.9486 8.80933 14.9226H8.77039V14.8826C8.67758 13.6218 8.67423 12.3558 8.76038 11.0945V11.0547H8.80029C9.66619 11.003 10.5345 11.003 11.4004 11.0547H11.4403V11.0945C11.4513 11.2505 11.4713 12.7885 11.4803 12.9475C11.4847 13.0382 11.5239 13.1237 11.5896 13.1863C11.6553 13.2489 11.7426 13.2838 11.8334 13.2837H11.8544C11.9004 13.2815 11.9455 13.2699 11.9871 13.25C12.0286 13.2301 12.0657 13.202 12.0963 13.1675C12.1277 13.1332 12.1519 13.0931 12.1675 13.0493C12.1831 13.0056 12.1899 12.959 12.1874 12.9126C12.1874 12.8196 12.1663 11.3477 12.1603 11.2547L12.1514 11.1126L12.2043 11.1177C12.7583 11.1707 13.3193 11.2456 13.8723 11.3406L13.9073 11.3467V11.3816C14.0023 12.4555 14.0032 13.5356 13.9103 14.6096L13.9093 14.6136ZM14.3704 9.07254C14.8535 9.59326 15.2363 10.1989 15.4994 10.8587L15.5183 10.9065L15.4584 10.9217H15.4453L14.5564 10.7405V10.7075C14.4924 10.1755 14.4054 9.63955 14.2964 9.11355L14.2633 8.95364L14.3704 9.07254ZM15.4773 15.1407C15.2192 15.785 14.8466 16.3773 14.3773 16.8887L14.3633 16.9036H14.2833L14.2943 16.8496C14.4003 16.3336 14.4863 15.8077 14.5493 15.2857V15.2527L14.7053 15.2227L15.5054 15.0635L15.4773 15.1407ZM15.7513 14.2627V14.2815L15.7203 14.3006L14.6273 14.5176V14.4575C14.7013 13.485 14.7013 12.5083 14.6273 11.5357V11.4756L15.7024 11.6956H15.7563L15.7643 11.7317C15.9451 12.5648 15.941 13.4272 15.7523 14.2586L15.7513 14.2627Z" fill="currentColor" />
                <path d="M12.1874 12.9771C12.1864 13.059 12.1571 13.1381 12.1043 13.2007C12.0515 13.2634 11.9786 13.3059 11.898 13.3208C11.8174 13.3358 11.7343 13.3223 11.6625 13.2828C11.5908 13.2432 11.535 13.1799 11.5047 13.1038C11.4744 13.0277 11.4713 12.9433 11.4961 12.8653C11.521 12.7872 11.5723 12.7201 11.641 12.6756C11.7098 12.631 11.7918 12.6117 11.8732 12.6209C11.9546 12.6301 12.0304 12.6673 12.0875 12.7261C12.1527 12.7933 12.1886 12.8835 12.1874 12.9771Z" fill="currentColor" />
                <path d="M3.7865 17.7456C3.77044 17.6834 3.7381 17.6266 3.69275 17.5811C3.64739 17.5355 3.59073 17.5029 3.52856 17.4866C3.21763 17.4045 2.93429 17.2406 2.70813 17.012C2.48197 16.7833 2.32124 16.4983 2.24255 16.1865C2.22378 16.1097 2.17973 16.0414 2.11755 15.9924C2.05538 15.9435 1.97866 15.9167 1.89954 15.9165C1.87124 15.9165 1.84306 15.9199 1.81555 15.9265C1.75269 15.9419 1.69521 15.9744 1.64954 16.0203C1.60386 16.0661 1.5717 16.1236 1.55652 16.1865C1.47775 16.4985 1.3168 16.7836 1.09045 17.0122C0.864109 17.2408 0.580654 17.4047 0.269531 17.4866C0.192269 17.507 0.123956 17.5524 0.0751953 17.6157C0.0264342 17.679 0 17.7565 0 17.8364C0 17.9163 0.0264342 17.9941 0.0751953 18.0574C0.123956 18.1207 0.192269 18.1661 0.269531 18.1865C0.58047 18.2686 0.863807 18.4325 1.08997 18.6611C1.31613 18.8898 1.47686 19.1748 1.55554 19.4866C1.57464 19.5632 1.61878 19.6313 1.68103 19.6799C1.74328 19.7286 1.82005 19.7549 1.89905 19.7549C1.97804 19.7549 2.0547 19.7286 2.11694 19.6799C2.17919 19.6313 2.22346 19.5632 2.24255 19.4866C2.32132 19.1746 2.48227 18.8895 2.70862 18.6609C2.93496 18.4323 3.21842 18.2684 3.52954 18.1865C3.62194 18.1619 3.70087 18.1017 3.74902 18.019C3.79718 17.9364 3.8106 17.8382 3.7865 17.7456ZM2.54749 17.8745C2.31476 18.0389 2.11186 18.2419 1.94751 18.4746V18.6035L1.8645 18.4875C1.69795 18.2499 1.49122 18.043 1.25354 17.8765L1.20056 17.8396L1.25354 17.8025C1.49113 17.6358 1.69784 17.4292 1.8645 17.1917L1.90149 17.1396L1.93848 17.1917C2.10501 17.4293 2.31187 17.636 2.54956 17.8025L2.60254 17.8396L2.54749 17.8745Z" fill="currentColor" />
                <path d="M6.85082 5.31763V5.31079L6.8308 5.27173C6.8265 5.26382 6.82313 5.25528 6.82079 5.24658L5.43285 2.31567C5.4037 2.25563 5.35822 2.20505 5.30162 2.16968C5.24502 2.1343 5.17953 2.11561 5.11278 2.11572C5.06058 2.11601 5.00918 2.12754 4.9619 2.14966L4.93688 2.16357L1.89586 3.91455C1.81922 3.95932 1.76204 4.03124 1.73558 4.11597C1.70913 4.2007 1.71524 4.29213 1.75279 4.37256L2.6339 6.23462L2.56786 6.23975C2.36899 6.25459 2.17466 6.30632 1.99486 6.39258C1.69009 6.53444 1.43476 6.76447 1.26183 7.05273C1.16377 7.189 1.10396 7.34895 1.08849 7.51611C1.07302 7.68327 1.10243 7.85148 1.17381 8.00342C1.2452 8.15536 1.35595 8.28557 1.49449 8.38037C1.63303 8.47518 1.79442 8.53121 1.9619 8.54273C2.04192 8.55556 2.1228 8.56195 2.20384 8.56177C2.45824 8.56068 2.70922 8.5031 2.93883 8.39356C3.23876 8.25541 3.48657 8.02485 3.64586 7.7356C3.72417 7.59582 3.76877 7.43983 3.77623 7.27979C3.78369 7.11974 3.75379 6.96018 3.68883 6.81372C3.68283 6.80172 3.67581 6.78764 3.66881 6.77564C3.66424 6.76738 3.66056 6.75879 3.65782 6.74976L2.53087 4.3667L4.9558 2.96655L5.79186 4.73364L5.72679 4.73975C5.52824 4.75594 5.33445 4.80851 5.15489 4.89478C4.85033 5.03694 4.59508 5.26666 4.42186 5.55469C4.33044 5.6941 4.27475 5.85398 4.25975 6.02002C4.24475 6.18606 4.27085 6.35305 4.3358 6.50659C4.4387 6.68666 4.59007 6.83433 4.77269 6.93262C4.95531 7.03091 5.16192 7.07603 5.36888 7.06274C5.62114 7.06083 5.86995 7.00385 6.09789 6.89575C6.25113 6.84184 6.3922 6.75822 6.51305 6.64966C6.63389 6.5411 6.73203 6.40955 6.80199 6.26294C6.87195 6.11633 6.91226 5.9574 6.92064 5.79517C6.92902 5.63294 6.90531 5.47066 6.85082 5.31763ZM2.71788 6.93872C2.74542 6.94065 2.77272 6.94431 2.79979 6.94971C2.87335 6.95128 2.94334 6.98165 2.99461 7.03442C3.04589 7.0872 3.07438 7.15811 3.07384 7.23169C3.0735 7.29379 3.05232 7.35389 3.01378 7.40259C2.92621 7.55406 2.79281 7.67386 2.6328 7.74463C2.4982 7.81016 2.3505 7.84445 2.20079 7.84473C2.16069 7.84468 2.12063 7.84226 2.0808 7.83765C2.01726 7.84775 1.95224 7.83342 1.89879 7.79761C1.87112 7.77926 1.84746 7.75568 1.82909 7.72803C1.81071 7.70038 1.79806 7.66933 1.79186 7.63672C1.78334 7.59343 1.78631 7.5486 1.80052 7.50684C1.81474 7.46507 1.83963 7.42779 1.87279 7.39868C1.97525 7.23759 2.12168 7.10909 2.29479 7.02856C2.41227 6.97085 2.541 6.93965 2.67186 6.93677L2.71788 6.93872ZM6.20787 5.83862C6.13221 6.01485 5.99442 6.15733 5.82079 6.23877L5.79479 6.25366C5.65717 6.3187 5.50705 6.35293 5.35484 6.35376C5.28341 6.36358 5.21062 6.35482 5.14354 6.32837C5.07646 6.30192 5.0173 6.25857 4.97179 6.20264V6.19165C4.96151 6.14255 4.9619 6.09194 4.97276 6.04297C4.98363 5.994 5.00482 5.94786 5.0349 5.90772C5.13706 5.74489 5.28433 5.61525 5.45885 5.53467C5.57626 5.47826 5.70457 5.44834 5.83482 5.44678C5.90608 5.43829 5.9783 5.449 6.04393 5.47803C6.10956 5.50706 6.16622 5.55314 6.20787 5.61157V5.61865C6.22283 5.65337 6.23065 5.69071 6.23082 5.72852C6.23099 5.76632 6.22251 5.80377 6.20787 5.83862Z" fill="currentColor" />
                <path d="M18.1465 4.99658C18.1302 4.93458 18.0978 4.87785 18.0525 4.83252C18.0072 4.78719 17.9507 4.75483 17.8887 4.73853C17.5777 4.65647 17.2943 4.49254 17.0681 4.26392C16.842 4.03529 16.6812 3.75029 16.6025 3.43848C16.5836 3.3617 16.5397 3.29354 16.4775 3.24463C16.4154 3.19572 16.3386 3.16883 16.2595 3.16846C16.2306 3.1685 16.2018 3.17187 16.1736 3.17847C16.1109 3.1942 16.0537 3.22665 16.0081 3.27246C15.9624 3.31827 15.93 3.3757 15.9146 3.43848C15.8359 3.75029 15.6751 4.03529 15.449 4.26392C15.2228 4.49254 14.9396 4.65647 14.6287 4.73853C14.5508 4.75821 14.4815 4.8033 14.4321 4.8667C14.3828 4.9301 14.356 5.00803 14.356 5.08838C14.356 5.16873 14.3828 5.24691 14.4321 5.3103C14.4815 5.3737 14.5508 5.41879 14.6287 5.43848C14.9396 5.52053 15.2228 5.68446 15.449 5.91309C15.6751 6.14171 15.8359 6.42672 15.9146 6.73853C15.9333 6.81564 15.9774 6.88406 16.0398 6.93311C16.1022 6.98215 16.1792 7.00903 16.2585 7.00903C16.3379 7.00903 16.4149 6.98215 16.4773 6.93311C16.5397 6.88406 16.5838 6.81564 16.6025 6.73853C16.6812 6.42672 16.842 6.14171 17.0681 5.91309C17.2943 5.68446 17.5777 5.52053 17.8887 5.43848C17.9814 5.41426 18.0608 5.35416 18.1094 5.27148C18.1579 5.18881 18.1717 5.09038 18.1477 4.99756L18.1465 4.99658ZM16.9065 5.12646C16.6686 5.29264 16.4616 5.49918 16.2947 5.73657L16.2615 5.71338L16.2217 5.73755C16.0549 5.50032 15.8481 5.2938 15.6106 5.12744L15.5576 5.09058L15.6106 5.05347C15.8483 4.88651 16.0551 4.67935 16.2217 4.44141L16.2585 4.3894L16.2957 4.44141C16.4621 4.67943 16.6687 4.8866 16.9065 5.05347L16.9597 5.09058L16.9065 5.12646Z" fill="currentColor" />
                <path d="M20.0174 15.5441L19.9173 15.536L19.9234 15.5299C19.3448 15.4596 18.7791 15.3085 18.2425 15.081C18.156 15.0439 18.0581 15.0427 17.9707 15.0775C17.8834 15.1124 17.8136 15.1806 17.7764 15.267C17.7639 15.2971 17.7554 15.3287 17.7513 15.361L17.4405 17.7411L17.3824 17.7089C17.1981 17.6083 16.9966 17.5433 16.7884 17.517C16.7098 17.5063 16.6306 17.5009 16.5513 17.5009C16.2879 17.5012 16.028 17.5614 15.7913 17.6769C15.6315 17.7394 15.4914 17.8436 15.3855 17.9787C15.2797 18.1137 15.2121 18.2746 15.1895 18.4447C15.1669 18.6148 15.1904 18.788 15.2574 18.946C15.3244 19.1039 15.4324 19.2409 15.5703 19.3429C15.8927 19.6196 16.3035 19.772 16.7283 19.7721C17.0226 19.7975 17.316 19.7144 17.5535 19.5387C17.791 19.3631 17.9564 19.1068 18.0183 18.818V18.786L18.3953 15.8981L18.4483 15.9181C18.9445 16.099 19.4628 16.2119 19.9893 16.2541C20.0825 16.2528 20.1715 16.2148 20.2371 16.1486C20.3027 16.0824 20.3399 15.9933 20.3404 15.9001C20.3405 15.8112 20.3076 15.7254 20.2478 15.6596C20.1881 15.5937 20.1059 15.5526 20.0174 15.5441ZM17.3174 18.7231V18.7631C17.2944 18.8281 17.2517 18.8842 17.1953 18.924C17.0597 19.019 16.8968 19.0677 16.7312 19.0629C16.6864 19.0627 16.6419 19.0599 16.5974 19.0541C16.3976 19.0287 16.2094 18.946 16.0555 18.8161C16.0123 18.7851 15.9763 18.7449 15.9502 18.6986C15.9242 18.6523 15.9085 18.601 15.9043 18.548V18.538C15.9173 18.433 16.0464 18.35 16.1214 18.31C16.2619 18.2431 16.4157 18.2091 16.5713 18.2101C16.6171 18.21 16.6628 18.2125 16.7083 18.2179C16.9187 18.2384 17.1133 18.3386 17.2522 18.498C17.3024 18.5616 17.3266 18.6425 17.3174 18.7231Z" fill="currentColor" />
            </svg>


        )
    }
    return (
        <div className="play-app-container" ref={appContainerRef}>
            {/* --- NEW: The Portrait/Mobile Restriction Overlay --- */}
            {isMobilePortrait && (
                <div className="portrait-overlay">
                    <div className="completion-content">
                        <div className="portrait-icon">
                            <svg viewBox="0 0 24 24" width="80" height="80">
                                <rect
                                    x="2"
                                    y="4"
                                    width="20"
                                    height="12"
                                    rx="2"
                                    fill="none"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M6 20h12M12 16v4"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <h1 className="portrait-title">Switch Device</h1>
                        <p className="portrait-subtitle">
                            Please access this content on a{' '}
                            <strong>PC, Laptop, or a Landscape Tablet</strong>.
                            Portrait ratios and mobile devices are not
                            supported.
                        </p>
                    </div>
                </div>
            )}

            {/* --- NEW: Floating Exit Preview Button --- */}
            {isPreviewMode && (
                <button
                    onClick={() => history.goBack()}
                    style={{
                        position: 'fixed',
                        top: '12vh', // Just below the header
                        right: '30px',
                        zIndex: 999999,
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '50px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <MdOutlineArrowBack size={18} /> Exit Preview
                </button>
            )}
            {/* HEADER — hidden in shell mode (SubprogramRunner renders it persistently) */}
            {!isShellMode && (
                <div className="play-header">
                    <HeaderComponent
                        showTimeline={isMake || isPreviewMode}
                        title={data.title || 'Play Mode'}
                        titlePosition="left"
                        help_btnPosition="right"
                        isGoBack={true}
                        goBack={goBack}
                        showHelp={true}
                        showBluetoothBtn={true}
                        showUsbBtn={true}
                        useUsbIcon={true}
                        currentStep={
                            hasSteps
                                ? Math.min(currentStep, totalSteps)
                                : undefined
                        }
                        totalSteps={hasSteps ? totalSteps : undefined}
                        stepTitles={
                            hasSteps
                                ? data?.steps?.map(
                                    (step) => step?.title || ''
                                ) || []
                                : []
                        }
                        onStepClick={(stepNum) => {
                            setUserInteractedThisStep(false)
                            setOutputStates(
                                buildStateUpToStep(
                                    stepNum - 1,
                                    data?.steps || []
                                )
                            )
                            ContentEngine.breakSequentialChain()
            setCurrentStep(stepNum)
                            if (aiVoiceover) playAudioForStep(stepNum)
                            setShowSubtitleDisplay(true)
                        }}
                    />
                </div>
            )}
            <main className="play-content">
                <div className="safe-area-container" ref={safeAreaRef}>
                    <svg className="wiring-layer">
                        {wires.map((wire) => (
                            <path
                                key={wire.id}
                                d={wire.path}
                                className="wire-path"
                            />
                        ))}
                    </svg>
                    {data.components.map(renderComponent)}
                </div>

                {/* AI Speaking Pill / Subtitle Box — morphs between shapes */}
                {currentInstruction && (
                    <div
                        ref={subtitleRef}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: !subtitleVisible
                                ? 'translateX(-50%) translateY(-120vh)'
                                : 'translateX(-50%)',
                            maxWidth: !showSubtitleDisplay ? '280px' : '600px',
                            maxHeight: !showSubtitleDisplay ? '44px' : '150px',
                            borderRadius: !showSubtitleDisplay
                                ? '20px'
                                : '20px',
                            padding: !showSubtitleDisplay
                                ? '0 18px'
                                : '12px 24px',
                            background: !showSubtitleDisplay
                                ? 'rgba(60, 60, 120, 0.13)'
                                : 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            display: 'grid',
                            gridTemplateRows: !showSubtitleDisplay
                                ? '44px'
                                : 'auto',
                            alignItems: 'center',
                            justifyItems: 'center',
                            overflow: 'hidden',
                            zIndex: 10001,
                            cursor: !showSubtitleDisplay
                                ? 'pointer'
                                : 'default',
                            opacity: !subtitleVisible ? 0 : 1,
                            pointerEvents: !subtitleVisible ? 'none' : 'auto',
                            transition: [
                                'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'border-radius 0.45s ease',
                                'padding 0.4s ease',
                                'background 0.45s ease',
                                'opacity 0.6s ease',
                            ].join(', '),
                        }}
                        onClick={
                            !showSubtitleDisplay
                                ? () => setShowSubtitleDisplay(true)
                                : undefined
                        }
                    >
                        {/* Pill content — visible when collapsed */}
                        <div
                            style={{
                                gridArea: '1/1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#2f2e65',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                                whiteSpace: 'nowrap',
                                opacity: !showSubtitleDisplay ? 1 : 0,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0.25s'
                                    : '0s',
                                pointerEvents: 'none',
                            }}
                        >
                            <BsStars size={20} />{' '}
                            {aiVoiceover
                                ? 'AI is speaking...'
                                : 'Click to expand'}
                        </div>
                        {/* Subtitle text — visible when expanded */}
                        <div
                            style={{
                                gridArea: '1/1',
                                fontSize: '1.1rem',
                                color: '#333',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'Halcyon Regular, sans-serif',
                                lineHeight: '1.5',
                                textAlign: 'center',
                                opacity: !showSubtitleDisplay ? 0 : 1,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0s'
                                    : '0.25s',
                                pointerEvents: 'none',
                                width: '100%',
                            }}
                        >
                            {aiVoiceover ? typewriterText : currentInstruction}
                        </div>
                    </div>
                )}

                {/* Bottom notice (please wait / please connect) */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '30px',
                        left: '50%',
                        transform: noticeText
                            ? 'translateX(-50%) translateY(0)'
                            : 'translateX(-50%) translateY(20px)',
                        padding: '12px 24px',
                        borderRadius: '20px',
                        background: 'rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        color: '#2f2e65',
                        fontWeight: 600,
                        fontSize: '1rem',
                        fontFamily: 'Halcyon_SemiBold, sans-serif',
                        whiteSpace: 'nowrap',
                        zIndex: 10001,
                        opacity: noticeText ? 1 : 0,
                        pointerEvents: 'none',
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                    }}
                >
                    {noticeText || ' '}
                </div>

                {/* {!isShellMode && ( */}
                <div className="toolbar-sidebar">
                    <button
                        className={`toolbar-button ${aiVoiceover ? 'active' : ''}`}
                        title="AI Voiceover"
                        onClick={() => {
                            handleAiVoiceoverToggle()
                            setSubtitleVisible(true)
                            setShowSubtitleDisplay(true)
                        }}
                    >

                        <ActionIcon />
                    </button>
                    <button
                        className="toolbar-button"
                        title="Fullscreen"
                        onClick={handleFullscreen}
                    >
                        <DanceIcon />
                    </button>
                </div>
                {/* )} */}
                <audio ref={audioRef} />
            </main>
            {/* FOOTER — hidden in shell mode (SubprogramRunner renders it
                persistently), and in single-step Kit Menu play (nothing to
                navigate). */}
            {!isShellMode && !hideFooter && (
                <footer className="play-footer">
                    <div className="step-counter">
                        {hasSteps
                            ? isFinalStep
                                ? 'Complete'
                                : `Step ${currentStep} of ${totalSteps}`
                            : ''}
                    </div>
                    <div className="step-title">
                        {hasSteps
                            ? currentStepData?.title || ''
                            : data.title || 'Play Mode'}
                    </div>
                    <div className="nav-buttons">
                        <button
                            className="nav-btn"
                            onClick={handleStepPrev}
                            disabled={isFirstStep && !dataProp}
                        >
                            <GrPrevious />
                            <span className="footer-btn-text">Previous</span>
                        </button>

                        {/* The Next Button dynamically disables if validations are not met */}
                        <div className="next-btn-wrapper">
                            {!isStepValid && (
                                <div className="next-btn-tooltip">
                                    <IoInformationCircle /> Complete the action
                                    to proceed
                                </div>
                            )}
                            <button
                                className={`nav-btn ${currentStepData?.isFreeStep ? 'free-step-highlight' : ''}`}
                                onClick={handleStepNext}
                                disabled={
                                    (isFinalStep && !dataProp) ||
                                    !isStepValid ||
                                    isTerminalStep
                                }
                            >
                                <span className="footer-btn-text">
                                    {isFinalStep ? 'Continue' : 'Next'}
                                </span>
                                <GrNext />
                            </button>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    )
}

export default PlayScreen
